import React, { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './scheduler.css';
import '../../../components/controls/stickyNotes/StickyNotes.css';
import { useDispatch, useSelector } from 'react-redux';
import { getCalendarEventsCatgories, getCalendarTimeEvents, saveCalendarTimeEvent, getCalenderEvents, deleteReminder, UsersAppointmentsGet } from '../../../apiServices/reducer/apiService';
import DeleteConfirmationModal from '../../../components/controls/stickyNotes/DeleteConfirmationModal';
import CustomSuccessModal from '../../../components/CustomSuccessModal';
import { showPmrError, showPmrWarning } from '../../../utils/swalUtils';
import { Dropdown, Form, Modal, Button } from 'react-bootstrap';

const localizer = momentLocalizer(moment);

// Stable day bounds for Week/Day views. Using `new Date()` as min made the
// grid start at "now" (e.g. 6:02 PM), so early-morning reminders (12:15 AM)
// were drawn in the wrong slot.
const CALENDAR_DAY_MIN = new Date(1970, 0, 1, 0, 0, 0);
const CALENDAR_DAY_MAX = new Date(1970, 0, 1, 23, 59, 59);
const DAY_SLOT_MS = 15 * 60 * 1000;

/** Local calendar day at midnight — avoids UTC/ISO day-shift bugs */
const toLocalMidnight = (d) => {
  if (!d && d !== 0) return null;
  if (typeof d === 'string') {
    const iso = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]), 0, 0, 0, 0);
    const us = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (us) return new Date(Number(us[3]), Number(us[1]) - 1, Number(us[2]), 0, 0, 0, 0);
  }
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return null;
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 0, 0, 0, 0);
};

const withLocalTime = (d, hours = 9, minutes = 0) => {
  const day = toLocalMidnight(d);
  if (!day) return null;
  day.setHours(hours, minutes, 0, 0);
  return day;
};

/** Map API / UI labels → once | daily | weekly | monthly | yearly */
const normalizeRecurrence = (value) => {
  const v = String(value || '').trim().toLowerCase();
  if (!v || v === 'select' || v === 'none' || v === 'no') return 'once';
  if (v === 'once' || v === 'one time' || v === 'onetime' || v === 'one-time') return 'once';
  if (v === 'daily' || v.startsWith('day')) return 'daily';
  if (v === 'weekly' || v.startsWith('week')) return 'weekly';
  if (v === 'monthly' || v.startsWith('month')) return 'monthly';
  if (v === 'yearly' || v === 'annually' || v.startsWith('year')) return 'yearly';
  return 'once';
};

/** Add months keeping day-of-month when possible (Jan 31 → Feb 28/29) */
const addMonthsClamped = (date, months) => {
  const src = date instanceof Date ? date : new Date(date);
  const day = src.getDate();
  const result = new Date(
    src.getFullYear(),
    src.getMonth() + months,
    1,
    src.getHours(),
    src.getMinutes(),
    src.getSeconds(),
    src.getMilliseconds()
  );
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(day, lastDay));
  return result;
};

const getMasterEventId = (event) => {
  if (!event) return null;
  if (event.parentId != null) return event.parentId;
  const id = event.id;
  if (id == null) return null;
  const s = String(id);
  if (s.includes('__')) return s.split('__')[0];
  return id;
};

/**
 * Expand reminders onto the calendar.
 * Once    = only the selected day
 * Daily   = every day (ongoing)
 * Weekly  = same weekday every week (Tue → next Tue → …)  ← only this rule is weekday-based
 * Monthly = every day for 1 month from start
 * Yearly  = every day for 1 year from start
 */
const expandRecurringEvent = (event, rangeStart, rangeEnd) => {
  if (!event?.start || !rangeStart || !rangeEnd) return [];
  const originStart = event.start instanceof Date ? new Date(event.start) : new Date(event.start);
  if (isNaN(originStart.getTime())) return [];

  const originEnd = event.end
    ? (event.end instanceof Date ? new Date(event.end) : new Date(event.end))
    : new Date(originStart.getTime() + DAY_SLOT_MS);
  const duration = Math.max(
    60 * 1000,
    Number.isFinite(originEnd.getTime() - originStart.getTime())
      ? originEnd.getTime() - originStart.getTime()
      : DAY_SLOT_MS
  );

  const rule = normalizeRecurrence(event.recurrence);
  const makeInstance = (occStart) => {
    const start = new Date(occStart);
    return {
      ...event,
      id: rule === 'once' ? event.id : `${event.id}__${start.getTime()}`,
      parentId: event.id,
      start,
      end: new Date(start.getTime() + duration),
      isRecurrenceInstance: rule !== 'once',
      recurrence: event.recurrence,
    };
  };

  // Once = only selected day
  if (rule === 'once') {
    if (originStart >= rangeStart && originStart <= rangeEnd) return [makeInstance(originStart)];
    return [];
  }

  // Weekly only: same weekday every week (Tue → next Tue → …)
  if (rule === 'weekly') {
    const instances = [];
    const weekday = originStart.getDay();
    const maxInstances = 520;
    for (let i = 0; i < maxInstances; i++) {
      const occStart = new Date(
        originStart.getFullYear(),
        originStart.getMonth(),
        originStart.getDate() + i * 7,
        originStart.getHours(),
        originStart.getMinutes(),
        originStart.getSeconds(),
        originStart.getMilliseconds()
      );
      if (occStart.getDay() !== weekday) {
        occStart.setDate(occStart.getDate() + (weekday - occStart.getDay()));
      }
      if (occStart > rangeEnd) break;
      if (occStart >= rangeStart) instances.push(makeInstance(occStart));
    }
    return instances;
  }

  // Daily / Monthly / Yearly = every day for a span (or ongoing for daily)
  let seriesEndExclusive = null;
  if (rule === 'monthly') {
    seriesEndExclusive = addMonthsClamped(originStart, 1);
  } else if (rule === 'yearly') {
    seriesEndExclusive = new Date(originStart);
    seriesEndExclusive.setFullYear(seriesEndExclusive.getFullYear() + 1);
  }
  // daily: no end — continue through visible range

  const hardEnd = seriesEndExclusive
    ? new Date(Math.min(seriesEndExclusive.getTime(), rangeEnd.getTime()))
    : rangeEnd;

  const instances = [];
  const maxInstances = 400;
  for (let i = 0; i < maxInstances; i++) {
    const occStart = new Date(
      originStart.getFullYear(),
      originStart.getMonth(),
      originStart.getDate() + i,
      originStart.getHours(),
      originStart.getMinutes(),
      originStart.getSeconds(),
      originStart.getMilliseconds()
    );
    if (occStart >= hardEnd) break;
    if (occStart > rangeEnd) break;
    if (occStart >= rangeStart) instances.push(makeInstance(occStart));
  }
  return instances;
};

const toDayKey = (d) => {
  if (!d) return null;
  // Prefer calendar date from YYYY-MM-DD to avoid UTC timezone day-shift
  if (typeof d === 'string') {
    const iso = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const us = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (us) {
      return `${us[3]}-${String(us[1]).padStart(2, '0')}-${String(us[2]).padStart(2, '0')}`;
    }
  }
  const dt = toLocalMidnight(d);
  if (!dt) return null;
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const parseTimeParts = (timeValue) => {
  if (!timeValue && timeValue !== 0) return null;
  const s = String(timeValue).trim().replace(/\u202f/g, ' ');
  const match = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (match) {
    let h = Number(match[1]);
    const min = Number(match[2]);
    const ap = (match[3] || '').toUpperCase();
    if (ap === 'PM' && h < 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;
    return { h, min };
  }
  const asDate = new Date(timeValue);
  if (!isNaN(asDate.getTime())) {
    return { h: asDate.getHours(), min: asDate.getMinutes() };
  }
  return null;
};

const combineDateAndTime = (dateValue, timeValue) => {
  const key = toDayKey(dateValue);
  if (!key) return null;
  const [y, mo, d] = key.split('-').map(Number);
  const parts = parseTimeParts(timeValue);
  if (!parts) return new Date(y, mo - 1, d, 9, 0, 0);
  return new Date(y, mo - 1, d, parts.h, parts.min, 0);
};

const calendarFormats = {
  // Reminder end time is default +1hr — show start only in Week/Day/Agenda labels
  eventTimeRangeFormat: ({ start, end }) => {
    if (start && end) {
      return `${localizer.format(start, 'h:mm A')} - ${localizer.format(end, 'h:mm A')}`;
    }
    return start ? localizer.format(start, 'h:mm A') : '';
  },
  eventTimeRangeStartFormat: ({ start }) =>
    localizer.format(start, 'h:mm A'),
  eventTimeRangeEndFormat: ({ end }) =>
    localizer.format(end, 'h:mm A'),
  agendaTimeFormat: (date) => localizer.format(date, 'h:mm A'),
  agendaTimeRangeFormat: ({ start }) =>
    localizer.format(start, 'h:mm A'),
  timeGutterFormat: (date) => localizer.format(date, 'h:mm A'),
  dayHeaderFormat: (date) => localizer.format(date, 'dddd, MMMM D, YYYY'),
  selectRangeFormat: ({ start, end }) =>
    `${localizer.format(start, 'h:mm A')} – ${localizer.format(end, 'h:mm A')}`,
};

const Scheduler = (props) => {
  const [events, setEvents] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalMessage, setSuccessModalMessage] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [showEventModal, setShowEventModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [voiceCommands, setVoiceCommands] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [showEventViewModal, setShowEventViewModal] = useState(false);
  const [viewEvent, setViewEvent] = useState(null);
  const [calendarCategories, setCalendarCategories] = useState([]);
  const [calendarTimeEvents, setCalendarTimeEvents] = useState([]);

  const recognitionRef = useRef(null);
  const dispatch = useDispatch();
  const [showVoiceHelp, setShowVoiceHelp] = useState(false);
  const [isModalListening, setIsModalListening] = useState(false);
  const [modalVoiceText, setModalVoiceText] = useState('');
  const modalRecognitionRef = useRef(null);
  const [activeReminder, setActiveReminder] = useState(null);
  const [queuedReminderCount, setQueuedReminderCount] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState('single'); // 'single' or 'all'
  const [selectedEvent, setSelectedEvent] = useState(null);
  // Add state for selectedDate
  const [selectedDate, setSelectedDate] = useState(null);
  // State for popover
  const [hoveredEventId, setHoveredEventId] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, placeAbove: false });
  const [calendarView, setCalendarView] = useState('month');
  const [calendarRange, setCalendarRange] = useState(() => {
    const start = new Date();
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start.getFullYear(), start.getMonth() + 2, 0, 23, 59, 59, 999);
    return { start, end };
  });
  const [useSampleEvents, setUseSampleEvents] = useState(false);
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState(false);
  const [timeSearch, setTimeSearch] = useState('');
  const [recurrenceOptions, setRecurrenceOptions] = useState([]);
  const [recurrenceError, setRecurrenceError] = useState(false);
  const [titleError, setTitleError] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [timeError, setTimeError] = useState(false);
  const timeDropdownRef = useRef(null);
  const token = props.token;
  const userId = props.userId;
  const activeCalendarUserIdRef = useRef(userId);
  activeCalendarUserIdRef.current = userId;

  // ---- Healthcare Calendar: health data coming from the dashboard ----
  const healthMedications = Array.isArray(props.medications) ? props.medications : [];
  const healthVitals = Array.isArray(props.vitals) ? props.vitals : [];
  const healthReports = Array.isArray(props.reports) ? props.reports : [];
  const onNavigateToReports = props.onNavigateToReports;

  // Upcoming appointments (fetched for the calendar date cells + day summary)
  const [healthAppointments, setHealthAppointments] = useState(
    Array.isArray(props.appointments) ? props.appointments : []
  );

  // Health Day panel (opens when a day's health chip is clicked)
  const [showHealthPanel, setShowHealthPanel] = useState(false);
  const [healthPanelDate, setHealthPanelDate] = useState(null);

  // Regular app popup (same CustomSuccessModal / Swal used across pages)
  const cleanPopupMessage = (message) =>
    String(message || '').replace(/^[^\w]+/, '').trim() || String(message || '');

  const showNotificationMessage = (message, typeOrDuration = 'success') => {
    const text = cleanPopupMessage(message);
    const type = typeof typeOrDuration === 'string' && ['success', 'error', 'warning'].includes(typeOrDuration)
      ? typeOrDuration
      : (String(message || '').includes('❌') ? 'error' : 'success');

    if (type === 'error') {
      showPmrError(text);
      return;
    }
    if (type === 'warning') {
      showPmrWarning(text);
      return;
    }
    setSuccessModalMessage(text);
    setShowSuccessModal(true);
  };

  // Initialize speech recognition
  useEffect(() => {
    loadCalendarEventsCatgories();
    loadCalendarTimeEvents();
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onstart = () => {
        setIsListening(true);
        setTranscript('Listening...');
      };

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0])
          .map(result => result.transcript)
          .join('');

        setTranscript(transcript);
        processVoiceCommand(transcript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        showPmrError('Voice recognition error.');
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    // Load saved events from localStorage
    const savedEvents = localStorage.getItem('medicalCalendarEvents');
    if (savedEvents) {
      const parsedEvents = JSON.parse(savedEvents);
      // Ensure all events have proper colors based on their category
      const eventsWithColors = parsedEvents.map(event => {
        if (!event.color && event.category) {
          const category = calendarCategories.find(cat => cat.name === event.category);
          if (category) {
            return { ...event, color: category.color };
          }
        }
        return event;
      });
      setEvents(eventsWithColors);
      // removed sample events logic
    }

    // Initialize voice commands
    setVoiceCommands([
      'set reminder',
      'add reminder',
      'create reminder',
      'schedule appointment',
      'add medical event',
      'book consultation',
      'set medication reminder',
      'medication reminder',
      'vaccination reminder',
      'vaccine reminder',
      'dental reminder',
      'dental appointment',
      'blood work',
      'lab test',
      'physical therapy',
      'therapy session',
      'eye exam',
      'vision check',
      'family appointment',
      'child appointment',
      'prescription refill',
      'medication refill',
      'mri scan',
      'imaging test',
      'cardiology',
      'heart check',
      'surgery',
      'operation',
      'follow up',
      'follow-up',
      'telemedicine',
      'virtual visit',
      'annual checkup',
      'yearly physical',
      'specialist',
      'consultation',
      'insulin',
      'diabetes',
      'elderly care',
      'senior care',
      'child vaccination',
      'kids vaccine',
      'health screening',
      'preventive care',
      'monthly injection',
      'monthly shot',
      'weekly treatment',
      'weekly therapy',
      'daily medication',
      'daily pill',
      'emergency visit',
      'export events',
      'clear all events',
      'search events',
      'filter by category'
    ]);
  }, []);
  const params = {
    token: token,
    userId: userId
  }
  const loadCalendarEventsCatgories = () => {
    dispatch(getCalendarEventsCatgories(params)).then((res) => {
      const newCalendarCategories = res.payload.map((item) => {
        return {
          name: item.CategoryName,
          color: item.CategoryColor,
          id: item.CategoryId
        }
      });
      setCalendarCategories(newCalendarCategories);
    });
  }
  const loadCalendarTimeEvents = () => {
    dispatch(getCalendarTimeEvents(params)).then((res) => {
      const newRecurrenceOptions = res.payload.map((item) => {
        return {
          value: item.ScheduleName,
          label: item.ScheduleName,
          scheduleId: item.ScheduleID
        }
      });
      setRecurrenceOptions(newRecurrenceOptions);
    });
  }
  const handleSaveCalendarTimeEvent = () => {
    // Reset all errors first
    setTitleError(false);
    setCategoryError(false);
    setTimeError(false);
    setRecurrenceError(false);

    // Validate title
    if (!currentEvent || !currentEvent.title || currentEvent.title.trim() === '') {
      setTitleError(true);
      return;
    }

    // Validate category
    if (!currentEvent.category || currentEvent.category.trim() === '' || currentEvent.category === 'Select') {
      setCategoryError(true);
      return;
    }

    // Validate time
    if (!currentEvent.userSelectedTime) {
      setTimeError(true);
      return;
    }

    // Validate recurrence selection
    if (!currentEvent.recurrence || currentEvent.recurrence === 'select') {
      setRecurrenceError(true);
      return;
    }
    const selectedCategory = calendarCategories.find(cat => cat.name === currentEvent.category);
    const durationMinutes = Math.max(1, Math.round((new Date(currentEvent.end) - new Date(currentEvent.start)) / 60000));

    // Find the ScheduleID for the selected recurrence
    const selectedRecurrence = recurrenceOptions.find(opt => opt.value === currentEvent.recurrence);
    const scheduleId = selectedRecurrence ? selectedRecurrence.scheduleId : null;

    // Check if this is an update operation (existing reminder has an ID)
    const isUpdate = currentEvent.id && !currentEvent.id.toString().startsWith('temp_');

    const payload = {
      token: token,
      UserId: userId,
      ReminderTitle: currentEvent.title,
      CategoryId: selectedCategory ? selectedCategory.id : null,
      ReminderTime: currentEvent.start,
      DurationMinutes: durationMinutes,
      RepeatId: scheduleId || null,
      Description: currentEvent.description || ''
    };

    // Add PatientRemainderId for update operations
    if (isUpdate) {
      payload.PatientRemainderId = currentEvent.id;
    }

    dispatch(saveCalendarTimeEvent(payload)).then((res) => {
      const message = isUpdate ? 'Reminder updated successfully' : 'Reminder saved successfully';
      showNotificationMessage(message);
      if (res.payload) {
        if (isUpdate) {
          // For updates, immediately update the local state to preserve displayEvents
          const updatedEvent = {
            ...currentEvent,
            title: currentEvent.title,
            category: currentEvent.category,
            description: currentEvent.description,
            start: new Date(currentEvent.start),
            end: new Date(currentEvent.end),
            recurrence: currentEvent.recurrence
          };

          setEvents(prevEvents =>
            prevEvents.map(event =>
              event.id === currentEvent.id ? updatedEvent : event
            )
          );
        } else {
          // For new events, reload from API
          loadCalenderEvents();
        }
      }
      setShowEventModal(false);
      setCurrentEvent(null);
    });
  }
  const loadCalenderEvents = () => {
    const requestedUserId = userId;
    dispatch(getCalenderEvents({ token, userId: requestedUserId })).then((res) => {
      if (String(activeCalendarUserIdRef.current ?? '') !== String(requestedUserId ?? '')) return;
      const apiItems = Array.isArray(res?.payload?.Table)
        ? res.payload.Table
        : (Array.isArray(res?.payload) ? res.payload : []);

      const newEvents = apiItems.map((item, index) => {
        const start = item.ReminderTime ? new Date(item.ReminderTime) : null;
        const durationMins = Number(item.DurationMinutes) > 0 ? Number(item.DurationMinutes) : 60;
        const end = start ? new Date(start.getTime() + durationMins * 60000) : null;
        const eventId = item.PatientRemainderId || `temp_${Date.now()}_${index}`;

        return {
          id: eventId,
          title: item.ReminderTitle || item.Title || '',
          category: item.CategoryName || item.Category || '',
          description: item.Description || '',
          recurrence: item.ScheduleName || 'select',
          color: item.CategoryColor || undefined,
          start,
          end,
        }
      });

      setEvents(newEvents);
      // sample events disabled
    });
  }

  const loadUpcomingAppointments = () => {
    const requestedUserId = userId;
    if (!requestedUserId || !token) return;
    dispatch(UsersAppointmentsGet({ UserID: requestedUserId, token }))
      .then((res) => {
        if (String(activeCalendarUserIdRef.current ?? '') !== String(requestedUserId ?? '')) return;
        const rows = Array.isArray(res?.payload) ? res.payload : [];
        setHealthAppointments(rows);
      })
      .catch((err) => {
        if (String(activeCalendarUserIdRef.current ?? '') !== String(requestedUserId ?? '')) return;
        console.error('Failed to load appointments for calendar:', err);
        setHealthAppointments([]);
      });
  };

  // Family dropdown changes the dashboard user. Reminders and appointments
  // were only loaded on first mount, so the calendar kept the previous member.
  useEffect(() => {
    if (!userId || !token) return;
    setEvents([]);
    setHealthAppointments([]);
    setShowHealthPanel(false);
    setHealthPanelDate(null);
    loadCalenderEvents();
    loadUpcomingAppointments();
  }, [userId, token]);

  // Refresh calendar appointments when widget add/edit/delete happens (no page reload)
  useEffect(() => {
    const onAppointmentsChanged = (event) => {
      const detail = event?.detail || {};
      // Optimistic update so the date cell lights up immediately
      if (detail.action === 'save' && detail.appointment) {
        setHealthAppointments((prev) => [
          ...prev,
          {
            ...detail.appointment,
            appt_id: detail.appointment.appt_id || `temp_${Date.now()}`,
          },
        ]);
      } else if (detail.action === 'delete' && detail.apptId) {
        setHealthAppointments((prev) =>
          prev.filter((a) => a.appt_id !== detail.apptId)
        );
      }
      // Re-fetch from API shortly after so IDs and server data stay accurate
      setTimeout(() => loadUpcomingAppointments(), 350);
    };
    window.addEventListener('pmr:appointments-changed', onAppointmentsChanged);
    return () => window.removeEventListener('pmr:appointments-changed', onAppointmentsChanged);
  }, [userId, token]);

  // Keep appointments in sync if parent passes them later
  useEffect(() => {
    if (Array.isArray(props.appointments)) {
      setHealthAppointments(props.appointments);
    }
  }, [props.appointments]);

  // Function to update event colors when categories change
  const updateEventColors = (events, categories) => {
    return events.map(event => {
      if (event.category) {
        const category = categories.find(cat => cat.name === event.category);
        if (category) {
          return { ...event, color: category.color };
        }
      }
      return event;
    });
  };

  // Helper function to format time for display
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const date = new Date(timeString);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Helper function to generate time options
  const generateTimeOptions = () => {
    const times = [];
    const rawSearch = String(timeSearch || '').trim().toLowerCase().replace(/\u202f/g, ' ');
    const now = new Date();
    const selectedDate = currentEvent?.start ? new Date(currentEvent.start) : new Date();

    // Check if selected date is today
    const isToday = selectedDate.toDateString() === now.toDateString();

    // Parse search like "6", "6:15", "06:15", "6:15pm", "6 pm"
    const searchMatch = rawSearch
      ? rawSearch.replace(/\s+/g, ' ').match(/^(\d{1,2})(?::(\d{1,2}))?\s*(a\.?m\.?|p\.?m\.?)?$/i)
      : null;
    const searchHour = searchMatch ? Number(searchMatch[1]) : null;
    const searchMinuteRaw = searchMatch && searchMatch[2] !== undefined ? searchMatch[2] : null;
    const searchPeriod = searchMatch && searchMatch[3]
      ? searchMatch[3].replace(/\./g, '').toLowerCase()
      : null;

    const matchesSearch = (hour, minute, displayTime) => {
      if (!rawSearch) return true;

      const displayNorm = displayTime.toLowerCase().replace(/\u202f/g, ' ').replace(/\s+/g, ' ').trim();
      const compactDisplay = displayNorm.replace(/\s+/g, '');
      const compactSearch = rawSearch.replace(/\s+/g, '');

      // Label prefix match: "6:15" → 6:15 AM / 6:15 PM only (not 4:15 / 16:15)
      if (compactDisplay.startsWith(compactSearch) || displayNorm.startsWith(rawSearch)) {
        return true;
      }

      if (!searchMatch || Number.isNaN(searchHour)) {
        return false;
      }

      if (searchHour < 0 || searchHour > 23) return false;

      const hour12 = hour % 12 === 0 ? 12 : hour % 12;
      const isAM = hour < 12;
      const isPM = hour >= 12;

      if (searchPeriod === 'am' && !isAM) return false;
      if (searchPeriod === 'pm' && !isPM) return false;

      const hourOk = searchPeriod
        ? hour12 === searchHour || (searchHour === 12 && hour12 === 12)
        : (hour12 === searchHour || hour === searchHour || (searchHour === 12 && (hour === 0 || hour === 12)));

      if (!hourOk) return false;

      if (searchMinuteRaw === null) return true;
      const minutePadded = String(minute).padStart(2, '0');
      if (searchMinuteRaw.length === 1) {
        return minutePadded.startsWith(searchMinuteRaw);
      }
      return minutePadded === searchMinuteRaw.padStart(2, '0');
    };

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        const date = new Date();
        date.setHours(hour, minute, 0, 0);
        const displayTime = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });

        // If it's today, filter out past times
        if (isToday) {
          const currentTime = now.getHours() * 60 + now.getMinutes();
          const optionTime = hour * 60 + minute;
          if (optionTime <= currentTime) {
            continue; // Skip past times
          }
        }

        if (matchesSearch(hour, minute, displayTime)) {
          times.push({
            value: timeString,
            label: displayTime
          });
        }
      }
    }

    return times;
  };

  // Handle time selection from dropdown
  const handleTimeSelect = (timeValue) => {
    const [h, m] = timeValue.split(':');
    let newStart;
    if (currentEvent?.start) {
      newStart = new Date(currentEvent.start);
      newStart.setHours(Number(h));
      newStart.setMinutes(Number(m));
      newStart.setSeconds(0);
      newStart.setMilliseconds(0);
    } else {
      // If start is null, use today as base
      newStart = new Date();
      newStart.setHours(Number(h));
      newStart.setMinutes(Number(m));
      newStart.setSeconds(0);
      newStart.setMilliseconds(0);
    }
    const newEnd = new Date(newStart.getTime() + (currentEvent?.end && currentEvent?.start ? (currentEvent.end - currentEvent.start) : 60 * 60 * 1000));
    setCurrentEvent(prev => ({ ...prev, start: newStart, end: newEnd, userSelectedTime: true }));
    setIsTimeDropdownOpen(false);
    setTimeSearch('');
    if (timeError) {
      setTimeError(false);
    }
  };

  // Delete confirmation functions
  const handleDeleteConfirm = () => {
    if (deleteType === 'single' && selectedEvent) {
      const masterId = getMasterEventId(selectedEvent);
      // Check if we have a valid ID from API
      if (masterId && !masterId.toString().startsWith('temp_')) {
        // Call API to delete single reminder
        const payload = {
          token: token,
          userId: userId,
          reminderId: masterId
        };

        dispatch(deleteReminder(payload)).then((res) => {
          if (res.payload) {
            // Remove from local state only after successful API call - use only ID for filtering
            const nextEvents = events.filter(e => String(e.id) !== String(masterId));
            setEvents(nextEvents);
            showNotificationMessage('Reminder deleted successfully');
          } else {
            showNotificationMessage('Failed to delete reminder.', 'error');
          }
        }).catch((error) => {
          console.error('Error deleting reminder:', error);
          showNotificationMessage('Error deleting reminder.', 'error');
        });
      } else {
        // If no valid ID, just remove from local state (for temporary/local reminders)
        const nextEvents = events.filter(e => String(e.id) !== String(masterId));
        setEvents(nextEvents);
        showNotificationMessage('Reminder deleted successfully');
      }
    } else if (deleteType === 'all') {
      // Delete all reminders via API calls
      const deletePromises = events.map(event => {
        if (event.id && !event.id.toString().startsWith('temp_')) {
          const payload = {
            token: token,
            userId: userId,
            reminderId: event.id
          };
          return dispatch(deleteReminder(payload));
        }
        return Promise.resolve();
      });

      Promise.all(deletePromises).then(() => {
        // Clear local storage and state after successful API calls
        try { localStorage.removeItem('medicalCalendarEvents'); } catch (e) { }
        setEvents([]);
        showNotificationMessage('All reminders deleted successfully');
      }).catch((error) => {
        console.error('Error deleting reminders:', error);
        showNotificationMessage('Error deleting some reminders.', 'error');
      });
    }

    // Close modal and reset state
    setShowDeleteModal(false);
    setSelectedEvent(null);
    setDeleteType('single');
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedEvent(null);
    setDeleteType('single');
  };

  /** Edit/delete must use the master reminder, not a recurring instance id */
  const resolveMasterEvent = (event) => {
    if (!event) return null;
    const masterId = getMasterEventId(event);
    const master = (events || []).find((e) => String(e.id) === String(masterId));
    const base = master || { ...event, id: masterId };
    return {
      ...base,
      id: masterId,
      start: base.start instanceof Date ? base.start : new Date(base.start),
      end: base.end instanceof Date ? base.end : new Date(base.end),
      recurrence: base.recurrence || event.recurrence || 'select',
      userSelectedTime: true,
    };
  };

  const openEditReminder = (event) => {
    const master = resolveMasterEvent(event);
    if (!master) return;
    setCurrentEvent(master);
    setShowEventModal(true);
  };

  const showDeleteConfirmation = (event, type = 'single') => {
    // Store master reminder so API delete uses the real id (not instance id)
    const master = type === 'single' && event ? resolveMasterEvent(event) : event;
    setSelectedEvent(master || event);
    setDeleteType(type);
    setShowDeleteModal(true);
  };

  // Function to get consistent category-based colors for reminders
  const getReminderColor = (event) => {
    // First, try to use the event's existing color
    if (event.color) {
      return event.color;
    }

    // If no color, get it from the category
    if (event.category) {
      const category = calendarCategories.find(cat => cat.name === event.category);
      if (category) {
        return category.color;
      }
    }

    // Fallback colors based on event type if category not found
    switch (event.type) {
      case 'medication':
        return '#10B981'; // Green
      case 'lab':
      case 'imaging':
      case 'therapy':
      case 'surgery':
        return '#EF4444'; // Red
      case 'vaccination':
      case 'dental':
      case 'screening':
        return '#F59E0B'; // Orange
      case 'family':
        return '#8B5CF6'; // Purple
      case 'appointment':
      case 'consultation':
      case 'followup':
      case 'annual':
      case 'emergency':
      case 'telemedicine':
        return '#3B82F6'; // Blue
      default:
        return '#e35336'; // Default green
    }
  };

  // Test function to demonstrate category-based coloring
  const testCategoryColors = () => {
    const testEvents = [
      {
        id: Date.now() + 1,
        title: 'Test Surgery',
        start: new Date(new Date().getTime() + 24 * 60 * 60 * 1000), // Tomorrow
        end: new Date(new Date().getTime() + 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000),
        type: 'surgery',
        category: 'Medical Procedures',
        description: 'Test medical procedure event'
      },
      {
        id: Date.now() + 2,
        title: 'Test Medication',
        start: new Date(new Date().getTime() + 2 * 24 * 60 * 60 * 1000), // Day after tomorrow
        end: new Date(new Date().getTime() + 2 * 24 * 60 * 60 * 1000 + 30 * 60 * 1000),
        type: 'medication',
        category: 'Medication Schedule',
        description: 'Test medication reminder'
      },
      {
        id: Date.now() + 3,
        title: 'Test Vaccination',
        start: new Date(new Date().getTime() + 3 * 24 * 60 * 60 * 1000),
        end: new Date(new Date().getTime() + 3 * 24 * 60 * 60 * 1000 + 30 * 60 * 1000),
        type: 'vaccination',
        category: 'Health Reminders',
        description: 'Test vaccination reminder'
      }
    ];

    // Add colors based on categories
    const eventsWithColors = testEvents.map(event => {
      const category = calendarCategories.find(cat => cat.name === event.category);
      return { ...event, color: category ? category.color : '#e35336' };
    });

    setEvents(prev => [...prev, ...eventsWithColors]);
    showNotificationMessage('🧪 Test events added to demonstrate category colors');
  };

  // Test function specifically for reminders list
  const testRemindersListColors = () => {
    const testReminders = [
      {
        id: Date.now() + 100,
        title: 'Blood Work - Fasting Required',
        start: new Date(new Date().getTime() + 4 * 24 * 60 * 60 * 1000),
        end: new Date(new Date().getTime() + 4 * 24 * 60 * 60 * 1000 + 60 * 60 * 1000),
        type: 'lab',
        category: 'Medical Procedures',
        description: 'Complete blood count and metabolic panel'
      },
      {
        id: Date.now() + 101,
        title: 'Insulin Injection - Evening',
        start: new Date(new Date().getTime() + 5 * 24 * 60 * 60 * 1000),
        end: new Date(new Date().getTime() + 5 * 24 * 60 * 60 * 1000 + 15 * 60 * 1000),
        type: 'medication',
        category: 'Medication Schedule',
        description: 'Evening insulin dose'
      },
      {
        id: Date.now() + 102,
        title: 'Dental Cleaning',
        start: new Date(new Date().getTime() + 6 * 24 * 60 * 60 * 1000),
        end: new Date(new Date().getTime() + 6 * 24 * 60 * 60 * 1000 + 60 * 60 * 1000),
        type: 'dental',
        category: 'Health Reminders',
        description: 'Regular dental checkup and cleaning'
      },
      {
        id: Date.now() + 103,
        title: 'Family Checkup - Sarah',
        start: new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000),
        end: new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000 + 60 * 60 * 1000),
        type: 'family',
        category: 'Family Health',
        description: 'Annual checkup for Sarah'
      }
    ];

    // Add colors based on categories
    const remindersWithColors = testReminders.map(event => {
      const category = calendarCategories.find(cat => cat.name === event.category);
      return { ...event, color: category ? category.color : '#e35336' };
    });

    setEvents(prev => [...prev, ...remindersWithColors]);
    showNotificationMessage('📋 Test reminders added to demonstrate category colors in reminders list');
  };

  // Test function for voice category detection
  const testVoiceCategoryDetection = () => {
    const testPhrases = [
      'Blood work tomorrow at 9 AM',
      'Surgery next week',
      'Insulin injection daily',
      'Dental cleaning next month',
      'Family checkup for Sarah'
    ];

    // Removed debug logs
    testPhrases.forEach(phrase => {

      const result = parseEventFieldsFromSpeech(phrase);

    });

    showNotificationMessage('🔍 Voice category detection test completed - check console for results');
  };

  // Update event colors when calendar categories change
  useEffect(() => {
    const safeEvents = Array.isArray(events) ? events : [];
    if (safeEvents.length > 0) {
      const updatedEvents = updateEventColors(safeEvents, calendarCategories);
      // Only update if colors actually changed to avoid infinite loops
      const hasColorChanges = updatedEvents.some((event, index) =>
        event.color !== safeEvents[index].color
      );
      if (hasColorChanges) {
        setEvents(updatedEvents);
      }
    }
  }, [calendarCategories]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showEventModal) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    };
  }, [showEventModal]);

  // Reset time dropdown / search when modal opens or closes
  useEffect(() => {
    setTimeSearch('');
    setIsTimeDropdownOpen(false);
    if (showEventModal) {
      timeDropdownRef.current = null;
    }
  }, [showEventModal]);

  // Save events to localStorage whenever events change
  useEffect(() => {
    localStorage.setItem('medicalCalendarEvents', JSON.stringify(Array.isArray(events) ? events : []));
  }, [events]);

  // Filter events based on search term and selected categories
  const safeEventsList = Array.isArray(events) ? events : [];
  const filteredEvents = safeEventsList.filter(event => {
    const matchesSearch = searchTerm === '' ||
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.description?.toLowerCase().includes(searchTerm.toLowerCase()); // Removed category from search

    const matchesCategory = selectedCategories.length === 0 ||
      selectedCategories.includes(event.category);

    return matchesSearch && matchesCategory;
  });

  const processVoiceCommand = (command) => {
    const lowerCommand = command.toLowerCase();

    // Helper function to get category color
    const getCategoryColor = (categoryName) => {
      const category = calendarCategories.find(cat => cat.name === categoryName);
      return category ? category.color : '#e35336';
    };

    // Enhanced reminder commands with better feedback
    if (lowerCommand.includes('set reminder') || lowerCommand.includes('add reminder') || lowerCommand.includes('create reminder')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: '',
        description: '',
        start: new Date(),
        end: new Date(new Date().getTime() + 60 * 60 * 1000), // 1 hour later
        type: 'reminder',
        category: 'Health Reminders',
        color: getCategoryColor('Health Reminders'),
        userSelectedTime: false
      });
      showNotificationMessage('📝 Opening reminder creation modal');
    } else if (lowerCommand.includes('schedule appointment') || lowerCommand.includes('add medical event') || lowerCommand.includes('book consultation')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: '',
        description: '',
        start: new Date(),
        end: new Date(new Date().getTime() + 60 * 60 * 1000), // 1 hour later
        type: 'appointment',
        category: 'Patient Appointments',
        color: getCategoryColor('Patient Appointments'),
        userSelectedTime: false
      });
      showNotificationMessage('🏥 Opening appointment scheduling modal');
    } else if (lowerCommand.includes('set medication reminder') || lowerCommand.includes('medication reminder')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: '',
        description: '',
        start: new Date(),
        end: new Date(new Date().getTime() + 30 * 60 * 1000), // 30 minutes later
        type: 'medication',
        category: 'Medication Schedule',
        color: getCategoryColor('Medication Schedule'),
        userSelectedTime: false
      });
      showNotificationMessage('💊 Opening medication reminder modal');
    } else if (lowerCommand.includes('export events')) {
      exportEvents();
      showNotificationMessage('📤 Exporting events to CSV');
    } else if (lowerCommand.includes('clear all events')) {
      if (window.confirm('Are you sure you want to clear all events?')) {
        setEvents([]);
        showNotificationMessage('🗑️ All events cleared');
      }
    } else if (lowerCommand.includes('search events')) {
      showNotificationMessage('🔍 Please speak the search term');
    } else if (lowerCommand.includes('filter by category')) {
      showNotificationMessage('📂 Please speak the category name');
    } else if (lowerCommand.includes('emergency visit')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Emergency Visit',
        description: 'Urgent medical attention required',
        start: new Date(),
        end: new Date(new Date().getTime() + 60 * 60 * 1000),
        type: 'emergency',
        category: 'Patient Appointments',
        color: getCategoryColor('Patient Appointments'),
        userSelectedTime: false
      });
      showNotificationMessage('🚨 Emergency visit scheduled');
    } else if (lowerCommand.includes('vaccination reminder') || lowerCommand.includes('vaccine reminder')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Vaccination Due',
        description: 'Vaccination reminder',
        start: new Date(),
        end: new Date(new Date().getTime() + 30 * 60 * 1000),
        type: 'vaccination',
        category: 'Health Reminders',
        color: getCategoryColor('Health Reminders'),
        userSelectedTime: false
      });
      showNotificationMessage('💉 Vaccination reminder created');
    } else if (lowerCommand.includes('dental reminder') || lowerCommand.includes('dental appointment')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Dental Checkup',
        description: 'Dental appointment reminder',
        start: new Date(),
        end: new Date(new Date().getTime() + 60 * 60 * 1000),
        type: 'dental',
        category: 'Health Reminders',
        color: getCategoryColor('Health Reminders'),
        userSelectedTime: false
      });
      showNotificationMessage('🦷 Dental reminder created');
    } else if (lowerCommand.includes('blood work') || lowerCommand.includes('lab test')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Blood Work',
        description: 'Laboratory test reminder',
        start: new Date(),
        end: new Date(new Date().getTime() + 60 * 60 * 1000),
        type: 'lab',
        category: 'Medical Procedures',
        color: getCategoryColor('Medical Procedures'),
        userSelectedTime: false
      });
      showNotificationMessage('🩸 Blood work reminder created');
    } else if (lowerCommand.includes('physical therapy') || lowerCommand.includes('therapy session')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Physical Therapy Session',
        description: 'Physical therapy appointment',
        start: new Date(),
        end: new Date(new Date().getTime() + 60 * 60 * 1000),
        type: 'therapy',
        category: 'Medical Procedures',
        color: getCategoryColor('Medical Procedures'),
        userSelectedTime: false
      });
      showNotificationMessage('🏋️ Physical therapy reminder created');
    } else if (lowerCommand.includes('eye exam') || lowerCommand.includes('vision check')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Eye Exam',
        description: 'Vision checkup reminder',
        start: new Date(),
        end: new Date(new Date().getTime() + 60 * 60 * 1000),
        type: 'eye',
        category: 'Health Reminders',
        color: getCategoryColor('Health Reminders'),
        userSelectedTime: false
      });
      showNotificationMessage('👁️ Eye exam reminder created');
    } else if (lowerCommand.includes('family appointment') || lowerCommand.includes('child appointment')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Family Health Appointment',
        description: 'Family member appointment',
        start: new Date(),
        end: new Date(new Date().getTime() + 60 * 60 * 1000),
        type: 'family',
        category: 'Family Health',
        color: getCategoryColor('Family Health'),
        userSelectedTime: false
      });
      showNotificationMessage('👨‍👩‍👧‍👦 Family appointment created');
    } else if (lowerCommand.includes('prescription refill') || lowerCommand.includes('medication refill')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Prescription Refill',
        description: 'Medication refill reminder',
        start: new Date(),
        end: new Date(new Date().getTime() + 30 * 60 * 1000),
        type: 'refill',
        category: 'Medication Schedule',
        color: getCategoryColor('Medication Schedule'),
        userSelectedTime: false
      });
      showNotificationMessage('💊 Prescription refill reminder created');
    } else if (lowerCommand.includes('mri scan') || lowerCommand.includes('imaging test')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'MRI Scan',
        description: 'Imaging test appointment',
        start: new Date(),
        end: new Date(new Date().getTime() + 90 * 60 * 1000), // 1.5 hours
        type: 'imaging',
        category: 'Medical Procedures',
        color: getCategoryColor('Medical Procedures'),
        userSelectedTime: false
      });
      showNotificationMessage('🔬 MRI scan reminder created');
    } else if (lowerCommand.includes('cardiology') || lowerCommand.includes('heart check')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Cardiology Appointment',
        description: 'Heart health checkup',
        start: new Date(),
        end: new Date(new Date().getTime() + 60 * 60 * 1000),
        type: 'consultation',
        category: 'Patient Appointments',
        color: getCategoryColor('Patient Appointments'),
        userSelectedTime: false
      });
      showNotificationMessage('❤️ Cardiology appointment created');
    } else if (lowerCommand.includes('surgery') || lowerCommand.includes('operation')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Surgery',
        description: 'Surgical procedure',
        start: new Date(),
        end: new Date(new Date().getTime() + 120 * 60 * 1000), // 2 hours
        type: 'surgery',
        category: 'Medical Procedures',
        color: getCategoryColor('Medical Procedures'),
        userSelectedTime: false
      });
      showNotificationMessage('⚕️ Surgery reminder created');
    } else if (lowerCommand.includes('follow up') || lowerCommand.includes('follow-up')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Follow-up Visit',
        description: 'Follow-up appointment',
        start: new Date(),
        end: new Date(new Date().getTime() + 60 * 60 * 1000),
        type: 'followup',
        category: 'Patient Appointments',
        color: getCategoryColor('Patient Appointments'),
        userSelectedTime: false
      });
      showNotificationMessage('🔄 Follow-up visit created');
    } else if (lowerCommand.includes('telemedicine') || lowerCommand.includes('virtual visit')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Telemedicine Session',
        description: 'Virtual medical consultation',
        start: new Date(),
        end: new Date(new Date().getTime() + 45 * 60 * 1000), // 45 minutes
        type: 'telemedicine',
        category: 'Patient Appointments',
        color: getCategoryColor('Patient Appointments'),
        userSelectedTime: false
      });
      showNotificationMessage('💻 Telemedicine session created');
    } else if (lowerCommand.includes('annual checkup') || lowerCommand.includes('yearly physical')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Annual Checkup',
        description: 'Yearly physical examination',
        start: new Date(),
        end: new Date(new Date().getTime() + 60 * 60 * 1000),
        type: 'annual',
        category: 'Patient Appointments',
        color: getCategoryColor('Patient Appointments'),
        userSelectedTime: false
      });
      showNotificationMessage('📋 Annual checkup created');
    } else if (lowerCommand.includes('specialist') || lowerCommand.includes('consultation')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Specialist Consultation',
        description: 'Specialist appointment',
        start: new Date(),
        end: new Date(new Date().getTime() + 60 * 60 * 1000),
        type: 'consultation',
        category: 'Patient Appointments',
        color: getCategoryColor('Patient Appointments'),
        userSelectedTime: false
      });
      showNotificationMessage('👨‍⚕️ Specialist consultation created');
    } else if (lowerCommand.includes('insulin') || lowerCommand.includes('diabetes')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Insulin Injection',
        description: 'Diabetes medication reminder',
        start: new Date(),
        end: new Date(new Date().getTime() + 15 * 60 * 1000), // 15 minutes
        type: 'insulin',
        category: 'Medication Schedule',
        color: getCategoryColor('Medication Schedule'),
        userSelectedTime: false
      });
      showNotificationMessage('💉 Insulin reminder created');
    } else if (lowerCommand.includes('elderly care') || lowerCommand.includes('senior care')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Elderly Care Appointment',
        description: 'Senior care visit',
        start: new Date(),
        end: new Date(new Date().getTime() + 60 * 60 * 1000),
        type: 'elderly',
        category: 'Family Health',
        color: getCategoryColor('Family Health'),
        userSelectedTime: false
      });
      showNotificationMessage('👴 Elderly care appointment created');
    } else if (lowerCommand.includes('child vaccination') || lowerCommand.includes('kids vaccine')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Child Vaccination',
        description: 'Children vaccination appointment',
        start: new Date(),
        end: new Date(new Date().getTime() + 30 * 60 * 1000),
        type: 'childvaccine',
        category: 'Family Health',
        color: getCategoryColor('Family Health'),
        userSelectedTime: false
      });
      showNotificationMessage('👶 Child vaccination created');
    } else if (lowerCommand.includes('health screening') || lowerCommand.includes('preventive care')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Health Screening',
        description: 'Preventive health check',
        start: new Date(),
        end: new Date(new Date().getTime() + 60 * 60 * 1000),
        type: 'screening',
        category: 'Health Reminders',
        color: getCategoryColor('Health Reminders'),
        userSelectedTime: false
      });
      showNotificationMessage('🔍 Health screening created');
    } else if (lowerCommand.includes('monthly injection') || lowerCommand.includes('monthly shot')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Monthly Injection',
        description: 'Monthly medication injection',
        start: new Date(),
        end: new Date(new Date().getTime() + 30 * 60 * 1000),
        type: 'monthly',
        category: 'Medication Schedule',
        color: getCategoryColor('Medication Schedule'),
        userSelectedTime: false
      });
      showNotificationMessage('💉 Monthly injection reminder created');
    } else if (lowerCommand.includes('weekly treatment') || lowerCommand.includes('weekly therapy')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Weekly Treatment',
        description: 'Weekly therapy session',
        start: new Date(),
        end: new Date(new Date().getTime() + 60 * 60 * 1000),
        type: 'weekly',
        category: 'Medical Procedures',
        color: getCategoryColor('Medical Procedures'),
        userSelectedTime: false
      });
      showNotificationMessage('📅 Weekly treatment created');
    } else if (lowerCommand.includes('daily medication') || lowerCommand.includes('daily pill')) {
      setShowEventModal(true);
      setCurrentEvent({
        title: 'Daily Medication',
        description: 'Daily medication reminder',
        start: new Date(),
        end: new Date(new Date().getTime() + 15 * 60 * 1000), // 15 minutes
        type: 'daily',
        category: 'Medication Schedule',
        color: getCategoryColor('Medication Schedule'),
        userSelectedTime: false
      });
      showNotificationMessage('💊 Daily medication reminder created');
    }
  };

  const exportEvents = () => {
    setIsLoading(true);

    setTimeout(() => {
      const csvContent = [
        ['Title', 'Description', 'Start Date', 'End Date', 'Category', 'Type'],
        ...filteredEvents.map(event => [
          event.title,
          event.description || '',
          moment(event.start).format('YYYY-MM-DD HH:mm'),
          moment(event.end).format('YYYY-MM-DD HH:mm'),
          event.category,
          event.type
        ])
      ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `medical-calendar-events-${moment().format('YYYY-MM-DD')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      setIsLoading(false);
      showNotificationMessage('📤 Events exported successfully');
    }, 1000);
  };

  // Sample medical events generated in the future relative to today
  const futureDate = (daysFromNow, hour, minute) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + daysFromNow);
    d.setHours(hour, minute, 0, 0);
    return d;
  };

  // Removed sampleEvents (testing data)

  // Additional rolling sample events from now
  const nowPlusMinutes = (mins) => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + mins);
    d.setSeconds(0);
    d.setMilliseconds(0);
    return d;
  };

  // Removed extraSampleEvents (testing data)

  // Expand Daily / Weekly / Monthly / Yearly across the visible calendar range
  const expansionRange = useMemo(() => {
    const rawStart = calendarRange?.start ? new Date(calendarRange.start) : new Date();
    const rawEnd = calendarRange?.end ? new Date(calendarRange.end) : new Date();
    const rangeStart = toLocalMidnight(rawStart) || toLocalMidnight(new Date());
    rangeStart.setDate(rangeStart.getDate() - 7);
    let rangeEnd = toLocalMidnight(rawEnd) || toLocalMidnight(new Date());
    rangeEnd.setDate(rangeEnd.getDate() + 7);
    rangeEnd.setHours(23, 59, 59, 999);
    // Cover a full year so Yearly (every day for 1 year) can paint all days
    const minEnd = new Date();
    minEnd.setFullYear(minEnd.getFullYear() + 1);
    minEnd.setHours(23, 59, 59, 999);
    if (rangeEnd < minEnd) rangeEnd = minEnd;
    return { rangeStart, rangeEnd };
  }, [calendarRange]);

  const expandedFilteredEvents = useMemo(() => {
    const list = [];
    (filteredEvents || []).forEach((ev) => {
      let recurrence = ev.recurrence;
      if ((!recurrence || recurrence === 'select') && ev.repeatId != null) {
        const opt = (recurrenceOptions || []).find((o) => String(o.scheduleId) === String(ev.repeatId));
        if (opt) recurrence = opt.value;
      }
      list.push(...expandRecurringEvent({ ...ev, recurrence }, expansionRange.rangeStart, expansionRange.rangeEnd));
    });
    return list;
  }, [filteredEvents, expansionRange, recurrenceOptions]);

  // Use expanded recurring instances on the calendar
  const displayEvents = expandedFilteredEvents;

  // Utility to ensure all events have Date objects for start and end
  function ensureEventDates(eventsList) {
    return (eventsList || []).map(event => ({
      ...event,
      start: event.start instanceof Date ? event.start : new Date(event.start),
      end: event.end instanceof Date ? event.end : new Date(event.end),
    }));
  }

  // Use safeDisplayEvents everywhere you render events
  const safeDisplayEvents = ensureEventDates(displayEvents);

  // Day view: reminders as 15-min blocks + upcoming appointments at real date/time
  const dayGridEvents = useMemo(() => {
    const reminders = ensureEventDates(displayEvents).map((ev) => {
      const start = new Date(ev.start);
      let end = new Date(ev.end);
      const dur = end.getTime() - start.getTime();
      // Saved reminders often default to +1 hour — compress to one 15-min slot so they don't stack/overlap
      if (!Number.isFinite(dur) || dur <= 0 || dur >= 50 * 60 * 1000) {
        end = new Date(start.getTime() + DAY_SLOT_MS);
      }
      return {
        ...ev,
        start,
        end,
        resourceType: 'reminder',
        isAppointment: false,
      };
    });

    const appts = (healthAppointments || [])
      .map((a, i) => {
        const dateRaw = a.appt_date || a.ApptDate || a.appointment_date || a.date;
        const startRaw = a.appt_start || a.ApptStart || '';
        const endRaw = a.appt_end || a.ApptEnd || '';
        const start = combineDateAndTime(dateRaw, startRaw);
        if (!start || isNaN(start.getTime())) return null;
        let end = combineDateAndTime(dateRaw, endRaw);
        if (!end || isNaN(end.getTime()) || end <= start) {
          end = new Date(start.getTime() + DAY_SLOT_MS);
        }
        const title =
          a.location_name || a.LocationName || a.location || a.patient_name || 'Upcoming Appointment';
        return {
          id: `appt-${a.appt_id || a.ApptID || i}-${start.getTime()}`,
          title,
          start,
          end,
          type: 'upcoming_appointment',
          category: 'Appointment',
          resourceType: 'appointment',
          isAppointment: true,
          color: '#FFF59D',
          description: a.location_details || a.LocationDetails || '',
          location: a.location_name || a.LocationName || a.location || '',
          raw: a,
        };
      })
      .filter(Boolean);

    return [...reminders, ...appts];
  }, [displayEvents, healthAppointments]);

  const calendarEvents =
    calendarView === 'day' ? dayGridEvents : safeDisplayEvents;

  // Timer refs for scheduled alerts
  const reminderTimersRef = useRef([]);
  // Track which reminder occurrences already fired this session (avoid duplicates)
  const firedReminderKeysRef = useRef(new Set());
  // Queue when multiple reminders are due at the same time
  const reminderQueueRef = useRef([]);
  // Ref for view-only modal container (for focus trap and keyboard handling)
  const viewModalRef = useRef(null);

  // Keyboard accessibility for view-only modal (Esc to close, focus trap)
  useEffect(() => {
    if (!showEventViewModal) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowEventViewModal(false);
        setViewEvent(null);
      } else if (e.key === 'Tab' && viewModalRef.current) {
        const focusableSelectors = [
          'a[href]',
          'area[href]',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          'button:not([disabled])',
          'iframe',
          '[tabindex]:not([tabindex="-1"])',
          '[contenteditable="true"]'
        ].join(',');

        const focusable = Array.from(viewModalRef.current.querySelectorAll(focusableSelectors));
        if (focusable.length === 0) return;

        const firstEl = focusable[0];
        const lastEl = focusable[focusable.length - 1];
        const activeEl = document.activeElement;

        if (e.shiftKey) {
          if (activeEl === firstEl) {
            lastEl.focus();
            e.preventDefault();
          }
        } else {
          if (activeEl === lastEl) {
            firstEl.focus();
            e.preventDefault();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Set initial focus
    setTimeout(() => {
      if (viewModalRef.current) {
        const closeBtn = viewModalRef.current.querySelector('button[title="Close"]');
        const firstBtn = viewModalRef.current.querySelector('button');
        const target = closeBtn || firstBtn || viewModalRef.current;
        if (target && typeof target.focus === 'function') target.focus();
      }
    }, 0);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showEventViewModal, viewEvent]);

  // Show alert at the scheduled reminder time.
  // Re-runs when `events` changes so newly saved reminders get timers.
  // Same-time reminders are queued — closing one shows the next.
  // Popup stays open until the user clicks Close (no auto-dismiss).
  useEffect(() => {
    reminderTimersRef.current.forEach((timer) => clearTimeout(timer));
    reminderTimersRef.current = [];

    const MAX_TIMEOUT = 2147483647; // browser setTimeout limit (~24.8 days)
    const GRACE_MS = 60 * 1000; // still show if we land within 1 min after due

    const reminderKey = (event) => {
      const start = event.start instanceof Date ? event.start : new Date(event.start);
      return `${event.id ?? event.title}|${start.getTime()}`;
    };

    const enqueueReminder = (event) => {
      const key = reminderKey(event);
      if (firedReminderKeysRef.current.has(key)) return;
      firedReminderKeysRef.current.add(key);

      setActiveReminder((current) => {
        if (!current) {
          return event;
        }
        // Already showing a popup — queue this one for after Close
        reminderQueueRef.current = [...reminderQueueRef.current, event];
        setQueuedReminderCount(reminderQueueRef.current.length);
        return current;
      });
    };

    const scheduleFromEvents = () => {
      const now = Date.now();
      const rangeStart = new Date();
      rangeStart.setHours(0, 0, 0, 0);
      const rangeEnd = new Date();
      rangeEnd.setDate(rangeEnd.getDate() + 25);
      rangeEnd.setHours(23, 59, 59, 999);

      const occurrences = [];
      (events || []).forEach((event) => {
        occurrences.push(...expandRecurringEvent(event, rangeStart, rangeEnd));
      });

      occurrences.forEach((event) => {
        if (!event?.start) return;
        const start = event.start instanceof Date ? event.start : new Date(event.start);
        if (isNaN(start.getTime())) return;

        const msUntil = start.getTime() - now;
        const key = reminderKey(event);
        if (firedReminderKeysRef.current.has(key)) return;

        if (msUntil > 0 && msUntil <= MAX_TIMEOUT) {
          const timer = setTimeout(() => enqueueReminder(event), msUntil);
          reminderTimersRef.current.push(timer);
        } else if (msUntil <= 0 && msUntil > -GRACE_MS) {
          enqueueReminder(event);
        }
      });
    };

    scheduleFromEvents();

    const pollId = setInterval(() => {
      const now = Date.now();
      const rangeStart = new Date();
      rangeStart.setHours(0, 0, 0, 0);
      const rangeEnd = new Date();
      rangeEnd.setDate(rangeEnd.getDate() + 2);
      rangeEnd.setHours(23, 59, 59, 999);
      const occurrences = [];
      (events || []).forEach((event) => {
        occurrences.push(...expandRecurringEvent(event, rangeStart, rangeEnd));
      });
      occurrences.forEach((event) => {
        if (!event?.start) return;
        const start = event.start instanceof Date ? event.start : new Date(event.start);
        if (isNaN(start.getTime())) return;
        const msUntil = start.getTime() - now;
        if (msUntil <= 0 && msUntil > -GRACE_MS) {
          enqueueReminder(event);
        }
      });
    }, 15000);

    return () => {
      reminderTimersRef.current.forEach((timer) => clearTimeout(timer));
      reminderTimersRef.current = [];
      clearInterval(pollId);
    };
  }, [events]);

  const dismissActiveReminder = () => {
    const next = reminderQueueRef.current[0] || null;
    reminderQueueRef.current = reminderQueueRef.current.slice(1);
    setQueuedReminderCount(reminderQueueRef.current.length);
    setActiveReminder(next);
  };

  const eventStyleGetter = (event) => {
    // Get color based on category if event.color is not set
    let backgroundColor = event.color;

    if (!backgroundColor) {
      // Find the category and use its color
      const category = calendarCategories.find(cat => cat.name === event.category);
      if (category) {
        backgroundColor = category.color;

      } else {
        // Fallback colors based on event type if category not found
        switch (event.type) {
          case 'medication':
            backgroundColor = '#10B981'; // Green
            break;
          case 'lab':
          case 'imaging':
          case 'therapy':
          case 'surgery':
            backgroundColor = '#EF4444'; // Red
            break;
          case 'vaccination':
          case 'dental':
          case 'screening':
            backgroundColor = '#F59E0B'; // Orange
            break;
          case 'family':
            backgroundColor = '#8B5CF6'; // Purple
            break;
          case 'appointment':
          case 'consultation':
          case 'followup':
          case 'annual':
          case 'emergency':
          case 'telemedicine':
            backgroundColor = '#3B82F6'; // Blue
            break;
          default:
            backgroundColor = '#e35336'; // Default green
        }

      }
    } else {

    }

    const isDayGrid = calendarView === 'day';
    if (isDayGrid) {
      const isAppt = event.isAppointment || event.resourceType === 'appointment';
      return {
        className: isAppt ? 'hc-day-event hc-day-event--appt' : 'hc-day-event hc-day-event--reminder',
        style: {
          backgroundColor: isAppt ? '#f5f0ff' : '#fff7ed',
          borderRadius: '6px',
          opacity: 1,
          color: '#2d2420',
          border: isAppt ? '1px solid #ddd0f7' : '1px solid #f0d5c8',
          borderLeft: isAppt ? '4px solid #7c3aed' : '4px solid #e35336',
          display: 'block',
          fontSize: '11px',
          fontWeight: '700',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          padding: '3px 7px',
          lineHeight: 1.2,
          maxHeight: 'none',
          minHeight: '32px',
          overflow: 'visible',
        },
      };
    }

    return {
      style: {
        backgroundColor: backgroundColor,
        borderRadius: '4px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '12px',
        fontWeight: '600',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        padding: '2px 6px',
        lineHeight: 1.3,
        maxHeight: '38px',
        overflow: 'hidden',
      }
    };
  };
  const handleMouseEnter = (event) => {
    setHoveredEventId(event.id);
  };
  const handleMouseLeave = () => {
    setHoveredEventId(null);
  };

  // Custom slot selection handler — today and future only
  const handleSlotSelect = (slotInfo) => {
    const today = toLocalMidnight(new Date());
    const slotDate = toLocalMidnight(slotInfo.start);
    if (!slotDate || !today || slotDate < today) {
      // Past dates: do not open Add Reminder
      return;
    }

    handleSelect(slotInfo);
  };


  const handleSelect = ({ start, end }) => {
    // The clicked slot always wins. selectedDate only tracks the month the toolbar
    // navigated to, so using it here opens the modal on the wrong day — and silently
    // opens nothing at all when that day happens to be in the past.
    if (!start && !selectedDate) return;

    const slotStart = toLocalMidnight(start || selectedDate);
    if (!slotStart) return;
    const slotEnd = end ? new Date(end) : null;
    const spanMs = slotEnd ? slotEnd.getTime() - (start ? new Date(start).getTime() : slotStart.getTime()) : 0;
    const rawStart = start ? new Date(start) : new Date(selectedDate);
    const isMonthDayClick =
      calendarView === 'month' ||
      (rawStart.getHours() === 0 &&
        rawStart.getMinutes() === 0 &&
        spanMs >= 23 * 60 * 60 * 1000);

    let eventStart;
    let eventEnd;
    if (isMonthDayClick) {
      // Always build from Y/M/D so popup title matches the clicked calendar cell
      eventStart = withLocalTime(slotStart, 9, 0);
      eventEnd = new Date(eventStart.getTime() + 60 * 60 * 1000);
    } else {
      eventStart = rawStart;
      eventEnd =
        slotEnd && spanMs > 0 && spanMs < 23 * 60 * 60 * 1000
          ? slotEnd
          : new Date(eventStart.getTime() + DAY_SLOT_MS);
    }

    // Check if the selected date is in the past
    const today = toLocalMidnight(new Date());
    const compareDate = toLocalMidnight(eventStart);

    if (!compareDate || !today || compareDate < today) {
      // Past dates: do not open Add Reminder
      setShowEventModal(false);
      return;
    }

    // Get the default category color
    const defaultCategory = 'Select';
    const category = calendarCategories.find(cat => cat.name === defaultCategory);
    const defaultColor = category ? category.color : '#3B82F6';

    // Reset all validation errors when opening modal
    setTitleError(false);
    setCategoryError(false);
    setTimeError(false);
    setRecurrenceError(false);
    setShowEventModal(true);
    setCurrentEvent({
      title: '',
      description: '',
      start: eventStart,
      end: eventEnd,
      type: 'appointment',
      category: defaultCategory,
      color: defaultColor,
      recurrence: 'select',
      userSelectedTime: !isMonthDayClick,
    });
  };

  // Open Add Reminder for a calendar day — today and future only
  const openAddReminderForDate = (date) => {
    if (!date) return;
    const day = toLocalMidnight(date);
    if (!day) return;
    const today = toLocalMidnight(new Date());
    if (day < today) return;

    handleSelect({
      start: day,
      end: new Date(day.getTime() + 60 * 60 * 1000),
    });
  };

  const handleEventSelect = (event) => {
    if (event?.isAppointment) {
      if (event.start) {
        setHealthPanelDate(event.start);
        setShowHealthPanel(true);
      }
      return;
    }
    setViewEvent(event);
    setShowEventViewModal(true);
  };

  // Speech recognition logic for modal
  useEffect(() => {
    if (!showEventModal) return;
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    modalRecognitionRef.current = new SpeechRecognition();
    modalRecognitionRef.current.continuous = false;
    modalRecognitionRef.current.interimResults = false;
    modalRecognitionRef.current.lang = 'en-US';

    modalRecognitionRef.current.onstart = () => {
      setIsModalListening(true);
      setModalVoiceText('');
    };
    modalRecognitionRef.current.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(result => result[0])
        .map(result => result.transcript)
        .join('');
      setModalVoiceText(transcript);
      // Parse and update currentEvent fields
      const parsed = parseEventFieldsFromSpeech(transcript);
      setCurrentEvent(prev => ({
        ...prev,
        ...parsed
      }));

      // Show notification if category was detected
      if (parsed.category) {
        showNotificationMessage(`🎯 Category detected: ${parsed.category}`, 3000);
      }

      setIsModalListening(false);
    };
    modalRecognitionRef.current.onerror = () => {
      setIsModalListening(false);
    };
    modalRecognitionRef.current.onend = () => {
      setIsModalListening(false);
    };
    return () => {
      modalRecognitionRef.current && modalRecognitionRef.current.abort();
    };
  }, [showEventModal]);

  // When opening the modal, ensure currentEvent.start is null for new reminders
  useEffect(() => {
    if (showEventModal && (!currentEvent || !currentEvent.start)) {
      // Get the default category color
      const defaultCategory = 'Select';
      const category = calendarCategories.find(cat => cat.name === defaultCategory);
      const defaultColor = category ? category.color : '#F59E0B';

      // Reset all validation errors when opening modal
      setTitleError(false);
      setCategoryError(false);
      setTimeError(false);
      setRecurrenceError(false);

      setCurrentEvent({
        title: '',
        description: '',
        start: null,
        end: null,
        type: 'reminder',
        category: defaultCategory,
        color: defaultColor,
        userSelectedTime: false
      });
    }
    // eslint-disable-next-line
  }, [showEventModal]);

  const startModalVoiceRecognition = () => {
    if (modalRecognitionRef.current) {
      setIsModalListening(true);
      setModalVoiceText('');
      modalRecognitionRef.current.start();
    }
  };
  const stopModalVoiceRecognition = () => {
    if (modalRecognitionRef.current) {
      setIsModalListening(false);
      modalRecognitionRef.current.stop();
    }
  };

  // Robust parser for event fields from speech
  function parseEventFieldsFromSpeech(text) {
    let title = '';
    let description = '';
    let newStart = null;
    let newEnd = null;
    let foundTime = false;
    let detectedCategory = null;
    let detectedType = null;
    let originalText = text;

    // 1. Extract time (robust)
    let timeMatch = text.match(/(?:at\s*)?(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/i);
    if (!timeMatch) {
      if (/noon/i.test(text)) {
        timeMatch = ['noon', '12', '00', 'PM'];
      } else if (/midnight/i.test(text)) {
        timeMatch = ['midnight', '12', '00', 'AM'];
      }
    }
    if (timeMatch) {
      let hour = parseInt(timeMatch[1], 10);
      let minute = timeMatch[2] || '00';
      let ampm = timeMatch[3];
      if (ampm) {
        if (ampm.toUpperCase() === 'PM' && hour < 12) hour += 12;
        if (ampm.toUpperCase() === 'AM' && hour === 12) hour = 0;
      } else {
        if (hour <= 7 && /(evening|night|pm)/i.test(text)) hour += 12;
      }
      foundTime = true;
      if (currentEvent && currentEvent.start) {
        newStart = new Date(currentEvent.start);
        newStart.setHours(hour);
        newStart.setMinutes(parseInt(minute, 10));
        newStart.setSeconds(0);
        newStart.setMilliseconds(0);
        newEnd = new Date(newStart.getTime() + 60 * 60 * 1000);
      }
      // Remove the time phrase from the text
      text = text.replace(timeMatch[0], '').replace(/noon|midnight/i, '').trim();
    }

    // 2. Detect category and type from keywords
    const lowerText = text.toLowerCase();

    // Medical Procedures detection
    if (/(surgery|operation|blood work|lab test|lab work|imaging|mri|ct scan|x-ray|ultrasound|physical therapy|therapy session|dialysis|chemotherapy|radiation|biopsy|colonoscopy|endoscopy|cardiac catheterization|angioplasty|stent|pacemaker|defibrillator|transplant|amputation|appendectomy|hysterectomy|mastectomy|prostatectomy|cholecystectomy|hernia repair|gallbladder|appendix|tonsillectomy|adenoidectomy|cataract surgery|lasik|dental surgery|root canal|extraction|implant|orthopedic surgery|joint replacement|hip replacement|knee replacement|spinal surgery|brain surgery|heart surgery|lung surgery|liver surgery|kidney surgery|pancreas surgery|thyroid surgery|parathyroid surgery|adrenal surgery|pituitary surgery)/i.test(lowerText)) {
      detectedCategory = 'Medical Procedures';
      if (/(surgery|operation)/i.test(lowerText)) detectedType = 'surgery';
      else if (/(blood work|lab test|lab work)/i.test(lowerText)) detectedType = 'lab';
      else if (/(imaging|mri|ct scan|x-ray|ultrasound)/i.test(lowerText)) detectedType = 'imaging';
      else if (/(physical therapy|therapy session)/i.test(lowerText)) detectedType = 'therapy';
    }

    // Patient Appointments detection
    else if (/(appointment|checkup|consultation|visit|follow up|follow-up|annual|yearly|monthly|weekly|emergency|urgent|telemedicine|virtual visit|video call|phone call|consult|examination|assessment|evaluation|review|screening|preventive|wellness|primary care|specialist|cardiologist|dermatologist|endocrinologist|gastroenterologist|neurologist|oncologist|orthopedist|pediatrician|psychiatrist|psychologist|rheumatologist|urologist|gynecologist|obstetrician|ophthalmologist|otolaryngologist|pulmonologist|nephrologist|hematologist|immunologist|allergist|dietitian|nutritionist|therapist|counselor|social worker|case manager|nurse practitioner|physician assistant)/i.test(lowerText)) {
      detectedCategory = 'Patient Appointments';
      if (/(emergency|urgent)/i.test(lowerText)) detectedType = 'emergency';
      else if (/(telemedicine|virtual visit|video call|phone call)/i.test(lowerText)) detectedType = 'telemedicine';
      else if (/(follow up|follow-up)/i.test(lowerText)) detectedType = 'followup';
      else if (/(annual|yearly)/i.test(lowerText)) detectedType = 'annual';
      else if (/(consultation|consult|specialist)/i.test(lowerText)) detectedType = 'consultation';
      else detectedType = 'appointment';
    }

    // Medication Schedule detection
    else if (/(medication|medicine|pill|tablet|capsule|injection|shot|vaccine|vaccination|insulin|diabetes|blood pressure|hypertension|cholesterol|thyroid|antidepressant|antibiotic|painkiller|analgesic|anti-inflammatory|steroid|hormone|birth control|contraceptive|supplement|vitamin|mineral|herb|natural remedy|prescription|refill|dosage|dose|schedule|reminder|morning|evening|night|daily|weekly|monthly)/i.test(lowerText)) {
      detectedCategory = 'Medication Schedule';
      if (/(insulin|diabetes)/i.test(lowerText)) detectedType = 'insulin';
      else if (/(vaccine|vaccination)/i.test(lowerText)) detectedType = 'vaccination';
      else if (/(refill|prescription)/i.test(lowerText)) detectedType = 'refill';
      else if (/(daily)/i.test(lowerText)) detectedType = 'daily';
      else if (/(weekly)/i.test(lowerText)) detectedType = 'weekly';
      else if (/(monthly)/i.test(lowerText)) detectedType = 'monthly';
      else detectedType = 'medication';
    }

    // Health Reminders detection
    else if (/(vaccination|vaccine|flu shot|tetanus|hepatitis|pneumonia|shingles|covid|coronavirus|dental|teeth|cleaning|checkup|hygiene|oral health|eye exam|vision|glasses|contact lens|hearing|audiology|screening|mammogram|pap smear|colonoscopy|prostate|psa|bone density|osteoporosis|cholesterol|diabetes|blood sugar|a1c|blood pressure|heart|cardiovascular|lung|respiratory|skin|dermatology|mole|rash|acne|psoriasis|eczema|mental health|depression|anxiety|stress|counseling|therapy|psychiatry|psychology|sleep|insomnia|fatigue|energy|weight|obesity|bmi|exercise|fitness|workout|gym|yoga|pilates|meditation|mindfulness|wellness|preventive|health maintenance|annual physical|checkup|examination)/i.test(lowerText)) {
      detectedCategory = 'Health Reminders';
      if (/(vaccination|vaccine|flu shot|tetanus|hepatitis|pneumonia|shingles|covid|coronavirus)/i.test(lowerText)) detectedType = 'vaccination';
      else if (/(dental|teeth|cleaning|checkup|hygiene|oral health)/i.test(lowerText)) detectedType = 'dental';
      else if (/(eye exam|vision|glasses|contact lens)/i.test(lowerText)) detectedType = 'eye';
      else if (/(screening|mammogram|pap smear|colonoscopy|prostate|psa|bone density|osteoporosis)/i.test(lowerText)) detectedType = 'screening';
      else detectedType = 'screening';
    }

    // Family Health detection
    else if (/(family|child|children|kid|baby|infant|toddler|teen|teenager|adolescent|parent|mother|father|mom|dad|grandparent|grandmother|grandfather|grandma|grandpa|sibling|brother|sister|cousin|aunt|uncle|niece|nephew|spouse|husband|wife|partner|significant other|elderly|senior|geriatric|aging|maternal|paternal|pediatric|obstetric|prenatal|postnatal|maternity|paternity|adoption|foster|guardian|caregiver|home care|nursing home|assisted living|hospice|palliative|end of life|bereavement|grief|loss)/i.test(lowerText)) {
      detectedCategory = 'Family Health';
      if (/(child|children|kid|baby|infant|toddler|teen|teenager|adolescent|pediatric)/i.test(lowerText)) detectedType = 'family';
      else if (/(elderly|senior|geriatric|aging)/i.test(lowerText)) detectedType = 'elderly';
      else if (/(maternal|prenatal|postnatal|maternity|obstetric)/i.test(lowerText)) detectedType = 'family';
      else detectedType = 'family';
    }

    // 3. Extract description using keywords
    let descMatch = text.match(/(?:for|about|regarding|with|due to|because of)\s+(.+)$/i);
    if (descMatch) {
      description = descMatch[1].trim();
      title = text.replace(descMatch[0], '').trim();
    } else {
      title = text.trim();
      description = '';
    }

    // 4. Explicit cues (e.g., 'title is ...', 'description is ...', 'category is ...')
    let explicitTitle = originalText.match(/title is (.+?)(?:$|,|\.|description|time|category)/i);
    let explicitDesc = originalText.match(/description is (.+?)(?:$|,|\.|title|time|category)/i);
    let explicitCategory = originalText.match(/category is (.+?)(?:$|,|\.|title|time|description)/i);

    if (explicitTitle) title = explicitTitle[1].trim();
    if (explicitDesc) description = explicitDesc[1].trim();
    if (explicitCategory) {
      const categoryText = explicitCategory[1].trim().toLowerCase();
      // Try to match explicit category with our categories
      const matchedCategory = calendarCategories.find(cat =>
        cat.name.toLowerCase().includes(categoryText) ||
        categoryText.includes(cat.name.toLowerCase())
      );
      if (matchedCategory) {
        detectedCategory = matchedCategory.name;
      }
    }

    // 5. Clean up title and remove category keywords
    if (detectedCategory) {
      // Remove category-related words from title to avoid redundancy
      const categoryWords = detectedCategory.toLowerCase().split(' ');
      title = title.replace(new RegExp(`\\b(${categoryWords.join('|')})\\b`, 'gi'), '').trim();

      // Clean up common medical terms that might be redundant
      title = title.replace(/\b(medical|health|procedure|appointment|reminder|checkup|visit|consultation|examination|assessment|evaluation|screening|test|scan|work|therapy|session|dose|injection|shot|medication|medicine|pill|tablet|capsule)\b/gi, '').trim();

      // Clean up extra spaces and punctuation
      title = title.replace(/\s+/g, ' ').replace(/^[,\s]+|[,\s]+$/g, '');
    }

    const result = {
      title: title || '',
      description: description || ''
    };

    if (foundTime && newStart && newEnd) {
      result.start = newStart;
      result.end = newEnd;
    }

    if (detectedCategory) {
      result.category = detectedCategory;
      // Get the color for the detected category
      const category = calendarCategories.find(cat => cat.name === detectedCategory);
      if (category) {
        result.color = category.color;
      }
    }

    if (detectedType) {
      result.type = detectedType;
    }



    return result;
  }

  // Function to download reminders as Excel file
  const downloadRemindersAsExcel = () => {
    try {
      // Filter reminders based on current filter
      const remindersToExport = safeDisplayEvents.filter(event =>
        remindersCategoryFilter.includes('All') || remindersCategoryFilter.includes(event.category)
      );

      if (remindersToExport.length === 0) {
        showNotificationMessage('No reminders to export', 'warning');
        return;
      }

      // Create CSV content (Excel can open CSV files)
      let csvContent = 'Title,Description,Category,Type,Start Date,Start Time,End Date,End Time,Duration,Recurrence\n';

      remindersToExport.forEach(event => {
        const startDate = new Date(event.start);
        const endDate = new Date(event.end);

        // Format dates
        const startDateStr = startDate.toLocaleDateString();
        const endDateStr = endDate.toLocaleDateString();

        // Format times
        const startTimeStr = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const endTimeStr = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        // Calculate duration
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        let durationStr = '';
        if (diffDays === 1) durationStr = '1 day';
        else if (diffDays < 7) durationStr = `${diffDays} days`;
        else if (diffDays < 30) durationStr = `${Math.ceil(diffDays / 7)} weeks`;
        else durationStr = `${Math.ceil(diffDays / 30)} months`;

        // Escape quotes and commas in text fields
        const escapeCsvField = (field) => {
          if (!field) return '';
          return `"${field.toString().replace(/"/g, '""')}"`;
        };

        csvContent += `${escapeCsvField(event.title)},${escapeCsvField(event.description)},${escapeCsvField(event.category)},${escapeCsvField(event.type)},${startDateStr},${startTimeStr},${endDateStr},${endTimeStr},${durationStr},${escapeCsvField(event.recurrence || 'select')}\n`;
      });

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `reminders_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showNotificationMessage(`✅ Exported ${remindersToExport.length} reminders to Excel/CSV`, 'success');
      } else {
        // Fallback for older browsers
        showNotificationMessage('Download not supported in this browser', 'error');
      }
    } catch (error) {
      console.error('Error exporting reminders:', error);
      showNotificationMessage('Error exporting reminders', 'error');
    }
  };

  // Function to download reminders as detailed Excel with multiple sheets
  const downloadRemindersAsDetailedExcel = (remindersOverride = null) => {
    try {
      // Filter reminders based on current filter (or use an explicit day-scoped list)
      const remindersToExport = Array.isArray(remindersOverride)
        ? remindersOverride
        : safeDisplayEvents.filter(event =>
            remindersCategoryFilter.includes('All') || remindersCategoryFilter.includes(event.category)
          );

      if (remindersToExport.length === 0) {
        showNotificationMessage('No reminders to export', 'warning');
        return;
      }

      // Create detailed CSV with additional information
      let detailedCsvContent = 'Title,Description,Category,Start Date,Start Time,End Date,End Time,Duration (Days),Duration (Hours),Recurrence,Priority,Status\n';

      remindersToExport.forEach(event => {
        const startDate = new Date(event.start);
        const endDate = new Date(event.end);

        // Format dates
        const startDateStr = startDate.toLocaleDateString();
        const endDateStr = endDate.toLocaleDateString();

        // Format times
        const startTimeStr = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const endTimeStr = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        // Calculate detailed duration
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));

        // Determine priority based on category and type
        let priority = 'Medium';
        if (event.category === 'Medical Procedures' || event.type === 'emergency') priority = 'High';
        else if (event.category === 'Health Reminders' || event.type === 'screening') priority = 'Medium';
        else if (event.category === 'Medication Schedule') priority = 'High';
        else priority = 'Medium';

        // Determine status based on date
        const now = new Date();
        let status = 'Upcoming';
        if (startDate < now && endDate < now) status = 'Completed';
        else if (startDate <= now && endDate >= now) status = 'In Progress';
        else if (startDate < now.getTime() + 24 * 60 * 60 * 1000) status = 'Due Soon';

        // Escape quotes and commas in text fields
        const escapeCsvField = (field) => {
          if (!field) return '';
          return `"${field.toString().replace(/"/g, '""')}"`;
        };

        detailedCsvContent += `${escapeCsvField(event.title)},${escapeCsvField(event.description)},${escapeCsvField(event.category)},${startDateStr},${startTimeStr},${endDateStr},${endTimeStr},${diffDays},${diffHours},${escapeCsvField(event.recurrence || 'select')},${priority},${status}\n`;
      });

      // Create and download the detailed file
      const blob = new Blob([detailedCsvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const dateStamp = remindersOverride?.[0]?.start
          ? new Date(remindersOverride[0].start).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0];
        link.setAttribute('download', `Detailed_Day_Summary_${dateStamp}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showNotificationMessage(`✅ Exported ${remindersToExport.length} detailed reminders to Excel/CSV`, 'success');
      } else {
        showNotificationMessage('Download not supported in this browser', 'error');
      }
    } catch (error) {
      console.error('Error exporting detailed reminders:', error);
      showNotificationMessage('Error exporting detailed reminders', 'error');
    }
  };

  // Export a full Day Summary CSV (reminders + vitals + meds + reports)
  const exportDaySummaryDetailedCsv = (panelDate, dayReminders, dayData) => {
    try {
      const escapeCsvField = (field) => {
        if (field === null || field === undefined) return '';
        return `"${field.toString().replace(/"/g, '""')}"`;
      };

      const dateStr = new Date(panelDate).toLocaleDateString();
      const isoDate = new Date(panelDate).toISOString().split('T')[0];
      let csv = 'Type,Title,Details,Category,Time / Value,Extra,Date\n';
      let rowCount = 0;

      (dayReminders || []).forEach((event) => {
        const start = event.start ? new Date(event.start) : null;
        const timeStr = start
          ? start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
          : '';
        csv += `${escapeCsvField('Reminder')},${escapeCsvField(event.title || 'Reminder')},${escapeCsvField(event.description || '')},${escapeCsvField(event.category || '')},${escapeCsvField(timeStr)},${escapeCsvField(event.recurrence || '')},${escapeCsvField(dateStr)}\n`;
        rowCount += 1;
      });

      (dayData?.appointments || []).forEach((a) => {
        const timeRange = [a.startLabel, a.endLabel].filter(Boolean).join(' – ');
        csv += `${escapeCsvField('Appointment')},${escapeCsvField(a.location || 'Appointment')},${escapeCsvField(a.locationDetails || '')},${escapeCsvField('Upcoming Appointments')},${escapeCsvField(timeRange)},${escapeCsvField('')},${escapeCsvField(dateStr)}\n`;
        rowCount += 1;
      });

      const vitals = dayData?.vitals;
      if (vitals) {
        const vitalRows = [
          ['Blood Pressure', vitals.systolic && vitals.diastolic ? `${vitals.systolic}/${vitals.diastolic} mmHg` : 'N/A'],
          ['Heart Rate', vitals.heartRate != null ? `${vitals.heartRate} bpm` : 'N/A'],
          ['Blood Sugar', vitals.bloodSugar != null ? `${vitals.bloodSugar} mg/dL` : 'N/A'],
          ['Weight', vitals.weight != null ? `${vitals.weight} lbs` : 'N/A'],
          ['Waist', vitals.waist != null ? `${vitals.waist} in` : 'N/A'],
        ];
        vitalRows.forEach(([title, value]) => {
          csv += `${escapeCsvField('Vital')},${escapeCsvField(title)},${escapeCsvField('')},${escapeCsvField('Vitals')},${escapeCsvField(value)},${escapeCsvField('')},${escapeCsvField(dateStr)}\n`;
          rowCount += 1;
        });
      }

      (dayData?.meds || []).forEach((m) => {
        const title = m.MedicationName || m.medication || 'Medication';
        const details = [m.Dose || m.dose, m.MedicationRecurrence || m.frequency].filter(Boolean).join(' · ');
        csv += `${escapeCsvField('Medication')},${escapeCsvField(title)},${escapeCsvField(details)},${escapeCsvField('Medication Schedule')},${escapeCsvField('')},${escapeCsvField('')},${escapeCsvField(dateStr)}\n`;
        rowCount += 1;
      });

      (dayData?.reports || []).forEach((r) => {
        const title = r.name || r.report_name || 'Report';
        const details = [r.provider_name, r.group_name || r.category, r.clinic].filter(Boolean).join(' · ');
        csv += `${escapeCsvField('Provider Note')},${escapeCsvField(title)},${escapeCsvField(details)},${escapeCsvField(r.group_name || r.category || 'Provider Notes')},${escapeCsvField('')},${escapeCsvField('')},${escapeCsvField(dateStr)}\n`;
        rowCount += 1;
      });

      if (rowCount === 0) {
        showNotificationMessage('No day summary data to export', 'warning');
        return;
      }

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `Day_Summary_${isoDate}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showNotificationMessage(`✅ Exported day summary (${rowCount} rows) to CSV`, 'success');
    } catch (error) {
      console.error('Error exporting day summary:', error);
      showNotificationMessage('Error exporting day summary', 'error');
    }
  };

  // Add state for reminders filter
  const [remindersCategoryFilter, setRemindersCategoryFilter] = useState(['All']);
  // Add state for reminders filter search
  const [remindersCategorySearch, setRemindersCategorySearch] = useState('');

  // Handler to change calendar view date
  const handleDateChange = (date) => {
    setHoveredEventId(null);
    setSelectedDate(date);
  };

  // Handler for view changes — clear sticky hover popovers when switching Month/Week/Day/Agenda
  const handleViewChange = (view) => {
    setHoveredEventId(null);
    if (view) setCalendarView(view);
  };

  // Icon toolbar: Back / Today / Next (matches icon-nav design) + Month / Day
  const CustomToolbar = ({ label, onNavigate, onView, view, views }) => {
    const viewList = Array.isArray(views) ? views : Object.keys(views || {});
    return (
      <div className="rbc-toolbar hc-calendar-toolbar">
        <div className="hc-toolbar-nav">
          <button
            type="button"
            className="hc-toolbar-nav-btn"
            onClick={() => onNavigate('PREV')}
            title="Back"
            aria-label="Back"
          >
            <i className="fas fa-chevron-left" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="hc-toolbar-nav-btn hc-toolbar-nav-btn--today"
            onClick={() => onNavigate('TODAY')}
            title="Today"
            aria-label="Today"
          >
            <i className="far fa-calendar" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="hc-toolbar-nav-btn"
            onClick={() => onNavigate('NEXT')}
            title="Next"
            aria-label="Next"
          >
            <i className="fas fa-chevron-right" aria-hidden="true" />
          </button>
        </div>

        <span className="rbc-toolbar-label">{label}</span>

        <div className="rbc-btn-group hc-toolbar-views">
          {viewList.map((name) => (
            <button
              type="button"
              key={name}
              className={view === name ? 'rbc-active' : ''}
              onClick={() => onView(name)}
            >
              {name === 'month' ? (
                <><i className="fas fa-th" aria-hidden="true" /> Month</>
              ) : name === 'day' ? (
                <><i className="fas fa-bars" aria-hidden="true" /> Day</>
              ) : (
                name.charAt(0).toUpperCase() + name.slice(1)
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Custom event component with themed hover popover (Agenda / Week / Day)
  const EventWithPopover = ({ event }) => {
    const eventRef = useRef();
    const accent = getReminderColor(event);
    const eventKey = event?.id;

    const clearHoverIfThis = () => {
      setHoveredEventId((current) => (current === eventKey ? null : current));
    };

    // If this event unmounts (view change / navigate) while hovered, drop the popover
    useEffect(() => {
      return () => {
        setHoveredEventId((current) => (current === eventKey ? null : current));
      };
    }, [eventKey]);

    const handleMouseEnter = (e) => {
      e.stopPropagation();
      setHoveredEventId(eventKey);
      if (!eventRef.current) return;
      const rect = eventRef.current.getBoundingClientRect();
      const popoverWidth = 230;
      const gap = 8;
      let left = rect.left + rect.width / 2;
      left = Math.max(popoverWidth / 2 + 10, Math.min(left, window.innerWidth - popoverWidth / 2 - 10));
      const placeAbove = rect.bottom + 140 > window.innerHeight;
      setPopoverPosition({
        top: placeAbove ? rect.top - gap : rect.bottom + gap,
        left,
        placeAbove,
      });
    };

    const handleMouseLeave = (e) => {
      // Ignore leave→enter transitions inside the same trigger
      const related = e.relatedTarget;
      if (related && eventRef.current && eventRef.current.contains(related)) {
        return;
      }
      clearHoverIfThis();
    };

    let dateStr = '';
    let timeStr = '';
    let weekdayStr = '';
    let dayBlockTime = '';
    if (event.start) {
      const startDate = new Date(event.start);
      weekdayStr = startDate.toLocaleDateString(undefined, { weekday: 'short' });
      dateStr = startDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      timeStr = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
      // Day cards: start time only (no end time)
      dayBlockTime = timeStr;
    }

    const recurrenceLabel = event.recurrence && !['none', 'select', 'once', 'Once'].includes(String(event.recurrence))
      ? String(event.recurrence).charAt(0).toUpperCase() + String(event.recurrence).slice(1)
      : null;

    const isDayGrid = calendarView === 'day';
    const isAppt = event.isAppointment || event.resourceType === 'appointment';

    return (
      <div
        ref={eventRef}
        className={`hc-event-trigger${isDayGrid ? ' hc-event-trigger--day' : ''}${isAppt ? ' hc-event-trigger--appt' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onPointerLeave={handleMouseLeave}
      >
        {isDayGrid ? (
          <>
            <div className="hc-event-trigger__title hc-event-trigger__title--day">
              {(event.title || (isAppt ? 'Appointment' : 'Reminder')).toUpperCase()}
            </div>
            {dayBlockTime && (
              <div className="hc-event-trigger__time">{dayBlockTime}</div>
            )}
          </>
        ) : (
          <div className="hc-event-trigger__title">{event.title}</div>
        )}
        {hoveredEventId === eventKey && isDayGrid && (
          <div
            className={`hc-day-tip${popoverPosition.placeAbove ? ' hc-day-tip--above' : ''}`}
            style={{
              top: popoverPosition.placeAbove ? popoverPosition.top - 8 : popoverPosition.top,
              left: popoverPosition.left,
            }}
            role="tooltip"
          >
            {(event.title || '').toUpperCase()} {dayBlockTime}
          </div>
        )}
        {hoveredEventId === eventKey && !isDayGrid && (
          <div
            className={`hc-event-popover${popoverPosition.placeAbove ? ' hc-event-popover--above' : ''}`}
            style={{
              top: popoverPosition.top,
              left: popoverPosition.left,
              ['--hc-popover-accent']: accent,
            }}
            role="tooltip"
          >
            <div className="hc-event-popover__accent" />
            <div className="hc-event-popover__header">
              <div className="hc-event-popover__icon" style={{ background: `${accent}18`, color: accent }}>
                <i className="fas fa-bell" aria-hidden="true" />
              </div>
              <div className="hc-event-popover__heading">
                <div className="hc-event-popover__title">{event.title || 'Reminder'}</div>
                {event.category && (
                  <span className="hc-event-popover__badge" style={{ background: `${accent}18`, color: accent, borderColor: `${accent}33` }}>
                    {event.category}
                  </span>
                )}
              </div>
            </div>

            <div className="hc-event-popover__body">
              {(dateStr || timeStr) && (
                <div className="hc-event-popover__row">
                  <span className="hc-event-popover__row-icon" style={{ color: accent }}>
                    <i className="far fa-clock" aria-hidden="true" />
                  </span>
                  <div className="hc-event-popover__row-text">
                    <span className="hc-event-popover__value">
                      {[weekdayStr, dateStr].filter(Boolean).join(' · ')}
                    </span>
                    {timeStr && <span className="hc-event-popover__sub">{timeStr}</span>}
                  </div>
                </div>
              )}

              {recurrenceLabel && (
                <div className="hc-event-popover__row">
                  <span className="hc-event-popover__row-icon" style={{ color: accent }}>
                    <i className="fas fa-redo" aria-hidden="true" />
                  </span>
                  <div className="hc-event-popover__row-text">
                    <span className="hc-event-popover__value">Repeats {recurrenceLabel}</span>
                  </div>
                </div>
              )}

              {event.description && (
                <div className="hc-event-popover__row">
                  <span className="hc-event-popover__row-icon" style={{ color: accent }}>
                    <i className="far fa-sticky-note" aria-hidden="true" />
                  </span>
                  <div className="hc-event-popover__row-text">
                    <span className="hc-event-popover__value hc-event-popover__note">{event.description}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Recurrence options + validation state are declared near other useState hooks above.
  // When rendering events, expand recurring events into visible instances for the current view.

  // ------------------------------------------------------------------
  // Healthcare Calendar data model: map vitals / medications / provider
  // notes onto individual calendar days so they can be surfaced in the
  // grid and in the Health Day panel.
  // ------------------------------------------------------------------
  const vitalsByDay = useMemo(() => {
    const map = {};
    healthVitals.forEach((v) => {
      const key = toDayKey(v.LastUpdated || v.lastUpdated || v.date);
      if (!key) return;
      map[key] = {
        systolic: v.BloodPressure_Systolic ?? v.systolic ?? null,
        diastolic: v.BloodPressure_Diastolic ?? v.diastolic ?? null,
        heartRate: v.HeartRate ?? v.heartRate ?? null,
        bloodSugar: v.BloodSugar ?? v.bloodSugar ?? null,
        weight: v.Weight ?? v.weight ?? null,
        waist: v.WaistSize ?? v.waist ?? null,
        raw: v,
      };
    });
    return map;
  }, [healthVitals]);

  const reportsByDay = useMemo(() => {
    const map = {};
    healthReports.forEach((r) => {
      const key = toDayKey(r.date || r.report_date);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    // Newest / named first; keep category visible in order
    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => {
        const catA = String(a.group_name || a.category || '').toLowerCase();
        const catB = String(b.group_name || b.category || '').toLowerCase();
        if (catA !== catB) return catA.localeCompare(catB);
        return String(a.name || a.report_name || '').localeCompare(String(b.name || b.report_name || ''));
      });
    });
    return map;
  }, [healthReports]);

  const providerNoteCategoryStyle = (category) => {
    const c = String(category || '').toLowerCase();
    if (c.includes('blood')) return { accent: '#2563eb', soft: '#eef4ff', text: '#1e40af' };
    if (c.includes('cardio') || c.includes('heart')) return { accent: '#dc2626', soft: '#fef2f2', text: '#b91c1c' };
    if (c.includes('gastro')) return { accent: '#d97706', soft: '#fff7ed', text: '#b45309' };
    if (c.includes('neuro')) return { accent: '#7c3aed', soft: '#f5f3ff', text: '#5b21b6' };
    if (c.includes('prescript') || c.includes('medication')) return { accent: '#059669', soft: '#ecfdf5', text: '#047857' };
    if (c.includes('imaging') || c.includes('radiolog') || c.includes('x-ray') || c.includes('mri')) {
      return { accent: '#0891b2', soft: '#ecfeff', text: '#0e7490' };
    }
    return { accent: '#2563eb', soft: '#eef4ff', text: '#1e40af' };
  };

  const parseApptTimeLabel = (timeValue) => {
    if (!timeValue) return '';
    // Already a friendly label
    if (/am|pm/i.test(String(timeValue)) && !String(timeValue).includes('T')) {
      return String(timeValue).trim();
    }
    // HH:mm / HH:mm:ss
    const match = String(timeValue).match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
    if (match) {
      const h = Number(match[1]);
      const m = match[2];
      const d = new Date();
      d.setHours(h, Number(m), 0, 0);
      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    }
    const asDate = new Date(timeValue);
    if (!isNaN(asDate.getTime())) {
      return asDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    }
    return String(timeValue);
  };

  const appointmentsByDay = useMemo(() => {
    const map = {};
    healthAppointments.forEach((a) => {
      const key = toDayKey(a.appt_date || a.ApptDate || a.appointment_date || a.date);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push({
        id: a.appt_id || a.ApptID || `${key}-${map[key].length}`,
        date: a.appt_date || a.ApptDate,
        start: a.appt_start || a.ApptStart || '',
        end: a.appt_end || a.ApptEnd || '',
        startLabel: parseApptTimeLabel(a.appt_start || a.ApptStart),
        endLabel: parseApptTimeLabel(a.appt_end || a.ApptEnd),
        location: a.location_name || a.LocationName || a.location || 'Appointment',
        locationDetails: a.location_details || a.LocationDetails || '',
        raw: a,
      });
    });
    // Sort each day's appointments by start time
    Object.keys(map).forEach((key) => {
      map[key].sort((x, y) => String(x.start).localeCompare(String(y.start)));
    });
    return map;
  }, [healthAppointments]);

  // Show medication chip only on its start date (not every day after start)
  const medActiveOnDate = (med, date) => {
    const started = med.MedicationStartedFrom || med.startedFrom;
    if (!started) return false;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const s = new Date(started);
    if (isNaN(s.getTime())) return false;
    s.setHours(0, 0, 0, 0);
    return d.getTime() === s.getTime();
  };

  const getDayHealth = (date) => {
    const key = toDayKey(date);
    const vitals = key ? vitalsByDay[key] : null;
    const reports = key ? (reportsByDay[key] || []) : [];
    const appointments = key ? (appointmentsByDay[key] || []) : [];
    const meds = healthMedications.filter((m) => medActiveOnDate(m, date));
    return { key, vitals, reports, meds, appointments };
  };

  // Colour-code blood pressure so the panel/legend reads at a glance
  const bpStatus = (sys, dia) => {
    const s = Number(sys);
    const di = Number(dia);
    if (!s || !di) return { label: 'N/A', color: '#9ca3af' };
    if (s >= 140 || di >= 90) return { label: 'High', color: '#dc2626' };
    if (s >= 130 || di >= 85) return { label: 'Elevated', color: '#f59e0b' };
    if (s < 90 || di < 60) return { label: 'Low', color: '#2563eb' };
    return { label: 'Normal', color: '#2d6a4f' };
  };

  const openHealthPanel = (date) => {
    setHealthPanelDate(date);
    setShowHealthPanel(true);
  };

  // Compact per-day health details + scrollable reminders list in the month cell
  const CustomDateHeader = ({ label, date }) => {
    const { vitals, reports, meds, appointments } = getDayHealth(date);
    const hasVitals = !!vitals && (vitals.systolic || vitals.heartRate || vitals.bloodSugar || vitals.weight);
    const medCount = meds.length;
    const reportCount = reports.length;
    const apptCount = appointments.length;
    const hasAny = hasVitals || medCount > 0 || reportCount > 0 || apptCount > 0;

    const dayStart = toLocalMidnight(date);
    const dayEnd = dayStart ? new Date(dayStart.getFullYear(), dayStart.getMonth(), dayStart.getDate(), 23, 59, 59, 999) : null;
    const today = toLocalMidnight(new Date());
    const isPastDate = !!(dayStart && today && dayStart < today);
    const isFutureOrToday = !isPastDate;

    const dayEvents = (safeDisplayEvents || []).filter((ev) => {
      if (!ev?.start || !dayStart || !dayEnd) return false;
      const s = new Date(ev.start);
      return s >= dayStart && s <= dayEnd;
    });

    const openPanel = (e) => {
      e.stopPropagation();
      e.preventDefault();
      e.nativeEvent?.stopImmediatePropagation?.();
      openHealthPanel(date);
    };

    const openAddReminder = (e) => {
      // Chips / marks keep their own handlers
      if (
        e.target.closest('.hc-day-marks') ||
        e.target.closest('.hc-mark') ||
        e.target.closest('.hc-day-details') ||
        e.target.closest('.hc-chip')
      ) {
        return;
      }
      e.stopPropagation();
      e.preventDefault();
      e.nativeEvent?.stopImmediatePropagation?.();
      if (isFutureOrToday) {
        openAddReminderForDate(date);
      }
      // Past dates: no Add Reminder
    };

    const vitalMetrics = hasVitals
      ? [
          vitals.systolic && vitals.diastolic
            ? { key: 'bp', value: `${vitals.systolic}/${vitals.diastolic}`, unit: '', label: `Blood pressure ${vitals.systolic}/${vitals.diastolic} mmHg` }
            : null,
          vitals.heartRate
            ? { key: 'hr', value: String(vitals.heartRate), unit: 'bpm', label: `Heart rate ${vitals.heartRate} bpm` }
            : null,
        ].filter(Boolean)
      : [];

    const metabolicMetrics = hasVitals
      ? [
          vitals.bloodSugar
            ? { key: 'sugar', value: String(vitals.bloodSugar), unit: 'mg', label: `Blood sugar ${vitals.bloodSugar} mg/dL` }
            : null,
          vitals.weight
            ? { key: 'wt', value: String(vitals.weight), unit: 'lb', label: `Weight ${vitals.weight} lbs` }
            : null,
          vitals.waist
            ? { key: 'waist', value: String(vitals.waist), unit: 'in', label: `Waist ${vitals.waist} in` }
            : null,
        ].filter(Boolean)
      : [];

    const medsText = medCount === 1
      ? (meds[0].MedicationName || meds[0].medication || 'Medication')
      : `${medCount} medications`;

    const notesText = reportCount === 1
      ? [
          reports[0].name || reports[0].report_name || 'Provider note',
          reports[0].group_name || reports[0].category || null,
        ].filter(Boolean).join(' · ')
      : `${reportCount} provider notes`;

    const apptsText = apptCount === 1
      ? ([
          appointments[0].startLabel,
          appointments[0].location
        ].filter(Boolean).join(' · ') || 'Appointment')
      : `${apptCount} appointments`;

    const dayKey = toDayKey(date);

    return (
      <div
        className={`hc-date-header ${hasAny || dayEvents.length ? 'hc-date-header--has-data' : ''} ${isPastDate ? 'hc-date-header--past' : 'hc-date-header--bookable'}`}
        data-date={dayKey || undefined}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={openAddReminder}
      >
        <div className="hc-date-top">
          <span className="hc-date-label">{label}</span>
          {dayEvents.length > 0 && (
            <span
              className="hc-day-marks"
              onClick={openPanel}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openPanel(e); }}
              role="button"
              tabIndex={0}
            >
              <span className="hc-mark hc-mark--reminder" aria-label={`${dayEvents.length} reminder(s)`}>
                <i className="fas fa-bell"></i>
                {dayEvents.length > 1 && <b>{dayEvents.length}</b>}
              </span>
            </span>
          )}
        </div>
        {hasAny && (
          <div
            className="hc-day-details"
            onClick={openPanel}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openPanel(e); }}
            role="button"
            tabIndex={0}
          >
            {apptCount > 0 && (
              <span className="hc-chip hc-chip-appt">
                <i className="fas fa-calendar-check" aria-hidden="true"></i>
                {apptsText}
              </span>
            )}
            {(vitalMetrics.length > 0 || metabolicMetrics.length > 0) && (
              <div className="hc-health-snap" aria-label="Vitals and metabolics">
                {vitalMetrics.length > 0 && (
                  <div className="hc-health-snap__row">
                    {vitalMetrics.map((item) => (
                      <span key={item.key} className={`hc-metric hc-metric--${item.key}`} title={item.label}>
                        <span className="hc-metric__val">
                          {item.value}
                          {item.unit ? <small>{item.unit}</small> : null}
                        </span>
                      </span>
                    ))}
                  </div>
                )}
                {metabolicMetrics.length > 0 && (
                  <div className="hc-health-snap__row hc-health-snap__row--meta">
                    {metabolicMetrics.map((item) => (
                      <span key={item.key} className={`hc-metric hc-metric--${item.key}`} title={item.label}>
                        <span className="hc-metric__val">
                          {item.value}
                          {item.unit ? <small>{item.unit}</small> : null}
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            {medCount > 0 && (
              <span className="hc-chip hc-chip-med">{medsText}</span>
            )}
            {reportCount > 0 && (
              <span className="hc-chip hc-chip-note">{notesText}</span>
            )}
          </div>
        )}
      </div>
    );
  };

  // Background cell wrapper (RBC prop is dateCellWrapper + value, not dayCellWrapper/date).
  // Makes the full day background clickable so empty space opens Add Reminder.
  const CustomDateCellWrapper = ({ value, children }) => {
    const today = toLocalMidnight(new Date());
    const cellDate = toLocalMidnight(value);
    const isPastDate = !!(cellDate && today && cellDate < today);
    const dayKey = toDayKey(value);

    return (
      <div
        className={`hc-date-cell-wrap ${isPastDate ? 'hc-day-past' : 'hc-day-bookable'}`}
        data-date={dayKey || undefined}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          height: '100%',
          cursor: isPastDate ? 'default' : 'pointer',
          backgroundColor: isPastDate ? '#fafafa' : 'transparent',
          opacity: isPastDate ? 0.85 : 1,
        }}
        onMouseDown={(e) => {
          // Prevent RBC slot-select from racing our click (wrong day on some cells)
          if (!isPastDate) e.stopPropagation();
        }}
        onClick={(e) => {
          if (
            e.target.closest('.hc-day-marks') ||
            e.target.closest('.hc-mark') ||
            e.target.closest('.hc-day-details') ||
            e.target.closest('.hc-chip') ||
            e.target.closest('.rbc-event')
          ) {
            return;
          }
          if (!isPastDate) {
            e.preventDefault();
            e.stopPropagation();
            openAddReminderForDate(value);
          }
        }}
      >
        {children}
      </div>
    );
  };

  // 6. In the calendar render, use expanded recurring events for the current view

  // Match right-panel stack height so left/right bottoms align
  const [calendarHeight, setCalendarHeight] = useState('440px');
  const [containerHeight, setContainerHeight] = useState('520px');

  useEffect(() => {
    const calculateDimensions = () => {
      // Room for the date, appointment, vitals/metabolics snapshot, medication, and note.
      const containerH = Math.max(760, Math.min(860, (window.innerHeight || 900) - 120));
      const calH = Math.max(640, containerH - 72);
      setContainerHeight(`${containerH}px`);
      setCalendarHeight(`${calH}px`);
    };

    // Calculate on mount and resize
    calculateDimensions();

    // Add event listeners
    const handleResize = () => {
      setTimeout(calculateDimensions, 100); // Delay to ensure resize is complete
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // Handle zoom changes
    const handleZoom = () => {
      setTimeout(calculateDimensions, 200); // Delay for zoom to apply
    };

    window.addEventListener('zoom', handleZoom);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      window.removeEventListener('zoom', handleZoom);
    };
  }, []);

  return (
    <div className="scheduler-responsive-container" style={{ height: '100%', minHeight: containerHeight, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff', flex: 1 }}>
      {/* Calendar Header */}
      <div className="calendar-header">
        <h2 className="calendar-header__title">
          <i className="far fa-calendar-check"></i>
          HealthCare Calendar
        </h2>
      </div>

      {/* Symbol legend for the day cells */}
      <div className="hc-legend">
        <span className="hc-legend__label">Day markers</span>
        <span className="hc-legend__item">
        </span>
        <span className="hc-legend__item">
          <span className="hc-mark hc-mark--reminder"><i className="fas fa-bell"></i></span> Reminders
        </span>
        <span className="hc-chip hc-chip-appt"><i className="fas fa-calendar-check"></i> Upcoming Appointments</span>
        <span className="hc-legend__item hc-legend__metrics" title="Vitals and metabolics">
          <span className="hc-metric hc-metric--bp"><i className="fas fa-heartbeat" aria-hidden="true"></i><span className="hc-metric__val">BP</span></span>
          <span className="hc-metric hc-metric--hr"><i className="fas fa-heart" aria-hidden="true"></i><span className="hc-metric__val">HR</span></span>
          <span className="hc-metric hc-metric--sugar"><i className="fas fa-tint" aria-hidden="true"></i><span className="hc-metric__val">Sugar</span></span>
          <span className="hc-metric hc-metric--wt"><i className="fas fa-weight" aria-hidden="true"></i><span className="hc-metric__val">Wt</span></span>
          <span className="hc-metric hc-metric--waist"><i className="fas fa-ruler-horizontal" aria-hidden="true"></i><span className="hc-metric__val">Waist</span></span>
        </span>
        <span className="hc-legend__item"><span className="hc-chip hc-chip-med">Medications</span></span>
        <span className="hc-legend__item"><span className="hc-chip hc-chip-note">Provider notes</span></span>
        {/* <span className="hc-legend__hint">Click a day for the full summary</span> */}
      </div>

      <div className="calendar-main-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <div
          className="calendar-wrapper"
          style={{ flex: 1, width: '100%', overflow: 'hidden', minHeight: 0 }}
          onMouseLeave={() => setHoveredEventId(null)}
        >
          <Calendar
            style={{
              height: '100%',
              width: '100%',
              cursor: 'pointer',
              minHeight: '360px'
            }}
            localizer={localizer}
            formats={calendarFormats}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            eventPropGetter={eventStyleGetter}
            onSelectSlot={handleSlotSelect}
            onSelectEvent={handleEventSelect}
            selectable
            popup={true}
            defaultView="month"
            view={calendarView}
            onView={handleViewChange}
            views={['month', 'day']}
            step={15}
            timeslots={1}
            dayLayoutAlgorithm="no-overlap"
            min={CALENDAR_DAY_MIN}
            max={CALENDAR_DAY_MAX}
            scrollToTime={new Date(1970, 0, 1, 8, 0, 0)}
            tooltipAccessor={() => ''}
            components={{
              toolbar: CustomToolbar,
              event: EventWithPopover,
              dateCellWrapper: CustomDateCellWrapper,
              month: { dateHeader: CustomDateHeader },
              agenda: {
                time: ({ event }) => {
                  if (!event?.start) return null;
                  return new Date(event.start).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                  });
                },
              },
            }}
            onNavigate={handleDateChange}
            onRangeChange={(range) => {
              setHoveredEventId(null);
              if (Array.isArray(range)) {
                setCalendarRange({ start: range[0], end: range[range.length - 1] });
              } else if (range.start && range.end) {
                setCalendarRange({ start: range.start, end: range.end });
              }
            }}
          />
          {/* Reminders List (right) */}
          {/* <div className="reminders-panel" style={{
            height: 'auto',
            maxHeight: '160px',
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: '0 0 auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
              <h4 className="reminders-title" style={{ margin: 0, whiteSpace: 'nowrap' }}>
                📋 Medications
              </h4>
              {displayEvents.length > 0 && (
                <div className="hc-reminders-actions">
                  <button
                    type="button"
                    className="hc-reminder-btn hc-reminder-btn--export"
                    onClick={downloadRemindersAsDetailedExcel}
                    title="Download detailed reminders with priority and status as CSV"
                  >
                    <i className="fas fa-file-excel" style={{ fontSize: '12px' }}></i>
                    Export Detailed CSV
                  </button>
                  <button
                    type="button"
                    className="hc-reminder-btn hc-reminder-btn--clear"
                    onClick={() => {
                      showDeleteConfirmation(null, 'all');
                    }}
                    title="Clear all reminders"
                  >
                    <i className="fas fa-trash" style={{ fontSize: '12px' }}></i>
                    Clear All
                  </button>
                </div>
              )}
            </div>
            {displayEvents.length === 0 ? (
              <div className="hc-reminders-empty">No reminders yet.</div>
            ) : (
              <ul className="hc-reminders-list">
                {safeDisplayEvents
                  .filter(event => remindersCategoryFilter.includes('All') || remindersCategoryFilter.includes(event.category))
                  .map((event, idx) => {
                    const reminderColor = getReminderColor(event);



                    const isToday = event.start && (() => {
                      const d = new Date(event.start);
                      const now = new Date();
                      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
                    })();

                    return (
                      <li
                        key={event.id || idx}
                        className={`hc-reminder-card${isToday ? ' reminder-today-animate' : ''}`}
                        data-category={event.category}
                        style={{ borderLeft: `6px solid ${reminderColor}` }}
                      >
                        <div
                          className="hc-reminder-card__dot"
                          style={{ backgroundColor: reminderColor }}
                          title={event.category || 'Unknown Category'}
                        ></div>

                        <div className="hc-reminder-card__head">
                          <div
                            className="hc-reminder-card__title"
                            onClick={ev => {
                              ev.stopPropagation();
                              setViewEvent(event);
                              setShowEventViewModal(true);
                            }}
                            title={event.title || 'Click to view details'}
                          >
                            {/^Dr\.?\s+/i.test(event.title) && (
                              <span className="material-icons" style={{ color: 'var(--pmr-primary)', marginRight: 4 }} title="Doctor">medical_services</span>
                            )}
                            {event.title}
                          </div>
                          <div className="hc-reminder-card__actions">
                            <button
                              type="button"
                              title="Edit"
                              className="hc-reminder-icon-btn hc-reminder-icon-btn--edit"
                              onClick={ev => {
                                ev.stopPropagation();
                                openEditReminder(event);
                              }}
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              type="button"
                              title="Delete"
                              className="hc-reminder-icon-btn hc-reminder-icon-btn--delete"
                              onClick={ev => {
                                ev.stopPropagation();
                                showDeleteConfirmation(event, 'single');
                              }}
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        </div>

                        {event.category && (
                          <div className="hc-reminder-card__badge" style={{ backgroundColor: reminderColor }}>
                            {event.category}
                          </div>
                        )}

                        <div className="hc-reminder-card__meta">
                          <i className="far fa-clock"></i>
                          {event.start ? `${new Date(event.start).toLocaleDateString()} ${new Date(event.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}` : ''}
                        </div>
                        {event.description && (
                          <div className="hc-reminder-card__note">
                            <i className="far fa-sticky-note"></i>
                            <span>{event.description}</span>
                          </div>
                        )}
                      </li>
                    );
                  })}
              </ul>
            )}
          </div> */}
          {/* Reminders strip hidden — Medications / Lab / Vitals live in the right-side dashboard panel */}
          {/* <div className="reminders-panel hc-reminders-hidden" style={{ display: 'none' }} aria-hidden="true" /> */}
        </div>
      </div>

      {/* Event Modal */}
      {showEventModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(45, 30, 24, 0.45)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1050,
            padding: '20px',
            width: '100%',
            height: '100vh',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '12px',
              minWidth: '400px',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
              position: 'relative',
              zIndex: 1050

            }}
            onClick={(e) => { e.stopPropagation() }}
          >
            <h3 style={{ marginBottom: '10px', color: 'var(--pmr-primary)', fontSize: '15px' }}>
              {currentEvent?.id && !currentEvent.id.toString().startsWith('temp_')
                ? `✏️ Edit Reminder for ${currentEvent?.start ? moment(currentEvent.start).format('MM/DD/YYYY') : ''}`
                : `📅 Add Reminder for ${currentEvent?.start ? moment(currentEvent.start).format('MM/DD/YYYY') : ''}`
              }
            </h3>

            {/* Close Button */}
            {/* <button
              onClick={() => {
                setShowEventModal(false);
                setCurrentEvent(null);
                setModalVoiceText('');
              }}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                fontSize: '20px',
                color: '#6c757d',
                cursor: 'pointer',
                padding: '5px',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={e => {
                e.target.style.backgroundColor = '#f8f9fa';
                e.target.style.color = '#495057';
              }}
              onMouseLeave={e => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6c757d';
              }}
              title="Close"
            >
              ×
            </button> */}
            {/* Voice input is now integrated with the Reminder Title field */}
            {/* Form Section */}
            <form onSubmit={e => {
              e.preventDefault();
              handleSaveCalendarTimeEvent();
            }}
              onClick={(e) => {
                // Close dropdown when clicking outside of it
                const target = e.target;
                const dropdown = target.closest('.dropdown');
                if (!dropdown && isTimeDropdownOpen) {
                  setIsTimeDropdownOpen(false);
                }
              }}
            >
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '12px'
                }}>
                  Reminder Title <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="text"
                    name="title"
                    value={currentEvent?.title || ''}
                    onChange={e => {
                      setCurrentEvent(prev => ({ ...prev, title: e.target.value }));
                      if (titleError) {
                        setTitleError(false);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        // Manually trigger validation
                        if (!currentEvent?.title || currentEvent.title.trim() === '') {
                          setTitleError(true);
                        }
                      }
                    }}
                    // placeholder="e.g., Annual Physical Examination"
                    className={titleError ? 'title-input-error' : ''}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: titleError ? '2px solid #dc3545 !important' : '2px solid #e9ecef',
                      borderRadius: '8px',
                      fontSize: '12px',
                      transition: 'all 0.2s ease',
                      backgroundColor: '#fff',
                      outline: 'none'
                    }}
                    ref={(el) => {
                      if (el && titleError) {
                        el.style.setProperty('border', '2px solid #dc3545', 'important');
                      }
                    }}
                  />
                  {/* <button
                    type="button"
                    onClick={isModalListening ? stopModalVoiceRecognition : startModalVoiceRecognition}
                    style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: isModalListening ? '#dc3545' : '#e35336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: isModalListening
                        ? '0 4px 12px rgba(220, 53, 69, 0.4)'
                        : '0 2px 8px rgba(227,83,54, 0.3)',
                      transition: 'all 0.3s ease',
                      flexShrink: 0
                    }}
                    title={isModalListening ? 'Stop Recording' : 'Start Voice Input'}
                  >
                    <i className="fas fa-microphone" style={{ fontSize: '14px' }}></i>
                  </button> */}
                </div>
                {/* Voice input status and transcribed text */}
                {isModalListening && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#dc3545',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#dc3545',
                      borderRadius: '50%',
                      animation: 'blink 1s infinite'
                    }}></div>
                    Listening... Speak now
                  </div>
                )}
                {modalVoiceText && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#495057'
                  }}>
                    <div style={{
                      fontSize: '11px',
                      color: '#6c757d',
                      fontWeight: '500',
                      marginBottom: '4px'
                    }}>
                      🎤 Voice Input Detected:
                    </div>
                    {modalVoiceText}

                    {/* Show detected category if available */}
                    {currentEvent?.category && (
                      <div style={{
                        marginTop: '6px',
                        padding: '4px 8px',
                        backgroundColor: `${currentEvent.color || '#e35336'}15`,
                        borderRadius: '6px',
                        fontSize: '11px',
                        color: currentEvent.color || '#e35336',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: currentEvent.color || '#e35336'
                        }}></div>
                        Detected: {currentEvent.category}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '12px'
                }}>
                  Category <span style={{ color: '#dc3545' }}>*</span>

                </label>
                <select
                  name="category"
                  value={currentEvent?.category || 'Select'}
                  onChange={e => {
                    const newCategory = e.target.value;
                    // Get the color for the selected category
                    const category = calendarCategories.find(cat => cat.name === newCategory);
                    const newColor = category ? category.color : '#e35336';

                    setCurrentEvent(prev => ({ ...prev, category: newCategory, color: newColor }));

                    if (categoryError && newCategory !== 'Select') {
                      setCategoryError(false);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: categoryError ? '2px solid #dc3545 !important' : '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '12px',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#fff'
                  }}
                  ref={(el) => {
                    if (el && categoryError) {
                      el.style.setProperty('border', '2px solid #dc3545', 'important');
                    }
                  }}
                >
                  {calendarCategories.map((category) => (
                    <option key={category.name} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div
                style={{ marginBottom: '12px', position: 'relative' }}
                onClick={(e) => e.stopPropagation()}
              >
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '12px'
                }}>
                  Time <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <Dropdown
                  show={isTimeDropdownOpen}
                  onToggle={(isOpen) => {
                    // Only allow opening when explicitly clicked, not when modal opens
                    if (isOpen && timeDropdownRef.current) {
                      setIsTimeDropdownOpen(true);
                    } else {
                      setIsTimeDropdownOpen(false);
                    }
                  }}
                  drop="down"
                  flip={false}
                >
                  <Dropdown.Toggle
                    ref={timeDropdownRef}
                    variant="outline-secondary"
                    className={`w-100 text-center ${timeError ? 'time-dropdown-error' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      timeDropdownRef.current = true;
                      setIsTimeDropdownOpen(!isTimeDropdownOpen);
                    }}
                    style={{
                      background: '#fff',
                      border: timeError ? '2px solid #dc3545 !important' : '2px solid #e9ecef',
                      color: '#495057',
                      fontWeight: 500,
                      minHeight: '32px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {currentEvent?.userSelectedTime ?
                      <span style={{ color: '#495057' }}>{formatTime(currentEvent.start)}</span> :
                      <span style={{ color: '#495057' }}>
                        {(() => {
                          const now = new Date();
                          const selectedDate = currentEvent?.start ? new Date(currentEvent.start) : new Date();
                          const isToday = selectedDate.toDateString() === now.toDateString();
                          const currentTime = now.getHours() * 60 + now.getMinutes();
                          const lastTimeToday = 23 * 60 + 45; // 11:45 PM

                          if (isToday && currentTime >= lastTimeToday) {
                            return "No time slots available today";
                          }
                          return "Reminder Time";
                        })()}
                      </span>
                    }
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    className="w-100"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      maxHeight: '200px',
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1051,
                      transform: 'none !important'
                    }}
                  >
                    {/* Search input for time - Fixed at top */}
                    <div style={{
                      padding: '10px 15px',
                      borderBottom: '1px solid #f1f1f1',
                      backgroundColor: '#fff',
                      zIndex: 1
                    }}>
                      <Form.Control
                        type="text"
                        placeholder="Search time (e.g., 2:30, PM, AM)"
                        value={timeSearch}
                        onChange={(e) => setTimeSearch(e.target.value)}
                        style={{
                          border: '1px solid #bfc9d9',
                          borderRadius: '6px',
                          fontSize: '14px',
                          padding: '6px 10px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {/* Scrollable time options container */}
                    <div style={{
                      maxHeight: '150px',
                      overflowY: 'auto'
                    }}>
                      {generateTimeOptions().map((timeOption) => (
                        <Dropdown.Item
                          key={timeOption.value}
                          onClick={() => handleTimeSelect(timeOption.value)}
                          style={{
                            padding: '10px 15px',
                            borderBottom: '1px solid #f1f1f1',
                            cursor: 'pointer',
                            fontWeight: 500,
                            color: '#495057',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f6faff'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                          {timeOption.label}
                        </Dropdown.Item>
                      ))}
                      {generateTimeOptions().length === 0 && timeSearch && (
                        <div style={{ padding: '10px 15px', color: '#666', fontStyle: 'italic' }}>
                          No times found matching "{timeSearch}"
                        </div>
                      )}
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              {/* Recurrence Dropdown */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '12px'
                }}>
                  Repeat <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <select
                  name="recurrence"
                  value={currentEvent?.recurrence || 'select'}
                  onChange={e => {
                    setCurrentEvent(prev => ({ ...prev, recurrence: e.target.value }));
                    if (recurrenceError) {
                      setRecurrenceError(false);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: recurrenceError ? '2px solid #dc3545' : '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '12px',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#fff'
                  }}
                >
                  {recurrenceOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {/* {recurrenceError && (
                  <div style={{
                    color: '#dc3545',
                    fontSize: '11px',
                    marginTop: '4px',
                    fontWeight: '500'
                  }}>
                    Please select the Repeat.
                  </div>
                )} */}
              </div>


              <div style={{ marginBottom: '15px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '12px'
                }}>
                  Description
                </label>
                <textarea
                  name="description"
                  value={currentEvent?.description || ''}
                  onChange={e => setCurrentEvent(prev => ({ ...prev, description: e.target.value }))}
                  // placeholder="e.g., Comprehensive health assessment with Dr. Johnson"
                  rows="1"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '12px',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#fff',
                    minHeight: '40px',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
              <div style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'center',
                paddingTop: '10px',
                borderTop: '1px solid #e9ecef',
                marginTop: '8px'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEventModal(false);
                    setCurrentEvent(null);
                    setModalVoiceText('');
                  }}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: 'var(--pmr-danger)',
                    color: 'var(--pmr-white)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => {
                    e.target.style.backgroundColor = '#b91c1c';
                  }}
                  onMouseLeave={e => {
                    e.target.style.backgroundColor = 'var(--pmr-danger)';
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'var(--pmr-btn-primary)',
                    color: 'var(--pmr-white)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(var(--pmr-btn-primary-rgb), 0.2)'
                  }}
                  onMouseEnter={e => {
                    e.target.style.backgroundColor = 'var(--pmr-btn-primary-hover)';
                    e.target.style.boxShadow = '0 4px 14px rgba(var(--pmr-btn-primary-rgb), 0.22)';
                  }}
                  onMouseLeave={e => {
                    e.target.style.backgroundColor = 'var(--pmr-btn-primary)';
                    e.target.style.boxShadow = '0 2px 4px rgba(var(--pmr-btn-primary-rgb), 0.2)';
                  }}
                >
                  {currentEvent?.id && !currentEvent.id.toString().startsWith('temp_') ? 'Update Reminder' : 'Add Reminder'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Success popup — same CustomSuccessModal used across pages */}
      <CustomSuccessModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        message={successModalMessage}
      />

      {/* Reminder Alert Modal — same status-popup layout as other pages */}
      <Modal
        show={!!activeReminder}
        onHide={dismissActiveReminder}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="modal-warning hc-reminder-alert-dialog"
        className="hc-reminder-alert-modal"
        backdropClassName="hc-reminder-alert-backdrop"
      >
        {activeReminder && (
          <>
            <Modal.Header className="pmr-modal-status-header border-0">
              <div className="w-100 d-flex flex-column align-items-center justify-content-center gap-1">
                <i className="fas fa-bell" style={{ fontSize: 40, color: 'var(--pmr-primary)' }} aria-hidden="true" />
                <span style={{ color: 'var(--pmr-primary)', fontWeight: 700, fontSize: 20 }}>Reminder!</span>
                {queuedReminderCount > 0 && (
                  <span className="hc-reminder-queue-badge">+{queuedReminderCount} more waiting</span>
                )}
              </div>
            </Modal.Header>

            <Modal.Body className="pmr-modal-status-body text-center">
              <div className="pmr-popup-message" style={{ fontWeight: 600, fontSize: '1.05rem', marginBottom: 6, textAlign: 'center' }}>
                {activeReminder.title}
              </div>
              {activeReminder.category && (
                <div style={{ color: 'var(--pmr-text-muted, #6c757d)', fontSize: 14, fontWeight: 500, marginBottom: 6, textAlign: 'center' }}>
                  {activeReminder.category}
                </div>
              )}
              {activeReminder.description && (
                <div style={{ color: 'var(--pmr-text, #495057)', fontSize: 14, fontWeight: 400, marginBottom: 8, textAlign: 'center' }}>
                  {activeReminder.description}
                </div>
              )}
              {activeReminder.start && (
                <div style={{ color: 'var(--pmr-text-muted, #6c757d)', fontSize: 14, fontWeight: 400, textAlign: 'center' }}>
                  {`${moment(activeReminder.start).format('MM/DD/YYYY')} ${moment(activeReminder.start).format('hh:mm A')}`}
                </div>
              )}
            </Modal.Body>

            <Modal.Footer className="pmr-modal-status-footer d-flex justify-content-center border-0">
              <Button
                variant="primary"
                className="no-hover p-2 px-4"
                onClick={dismissActiveReminder}
                autoFocus
              >
                {queuedReminderCount > 0 ? 'OK & show next' : 'OK'}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
      {/* View-Only Event Details Modal (div-based) */}
      {showEventViewModal && viewEvent && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeIn 0.3s',
          }}
          aria-modal="true"
          role="dialog"
          onClick={() => {
            setShowEventViewModal(false);
            setViewEvent(null);
          }}
        >
          <div
            ref={viewModalRef}
            style={{
              background: 'white',
              borderRadius: '18px',
              boxShadow: '0 8px 32px rgba(227,83,54,0.18)',
              padding: '20px 22px 18px 22px',
              minWidth: 480,
              maxWidth: 760,
              position: 'relative',
              borderTop: `6px solid ${getReminderColor(viewEvent)}`,
              animation: 'popIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                setShowEventViewModal(false);
                setViewEvent(null);
              }}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'none',
                border: 'none',
                fontSize: 20,
                color: '#6c757d',
                cursor: 'pointer',
                padding: 6,
                borderRadius: '50%'
              }}
              title="Close"
            >
              ×
            </button>
            <div style={{ textAlign: 'left' }}>
              {/* Header with icon and title */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: `${getReminderColor(viewEvent)}15`,
                    color: getReminderColor(viewEvent),
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)'
                  }}
                  title={viewEvent.category || 'Reminder'}
                >
                  <i
                    className={
                      (
                        viewEvent.type === 'medication' ? 'fas fa-pills' :
                          viewEvent.type === 'lab' || viewEvent.type === 'imaging' ? 'fas fa-vials' :
                            viewEvent.type === 'therapy' ? 'fas fa-dumbbell' :
                              viewEvent.type === 'surgery' ? 'fas fa-procedures' :
                                viewEvent.type === 'vaccination' ? 'fas fa-syringe' :
                                  viewEvent.type === 'family' ? 'fas fa-users' :
                                    viewEvent.type === 'telemedicine' ? 'fas fa-video' :
                                      viewEvent.type === 'emergency' ? 'fas fa-ambulance' :
                                        'far fa-calendar-check'
                      )
                    }
                    style={{ fontSize: 20 }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontWeight: 800, color: 'var(--pmr-primary)', fontSize: 20, lineHeight: 1.2, letterSpacing: 0.2 }}>{viewEvent.title || 'Untitled Event'}</div>
                  {viewEvent.category && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                      <span style={{
                        display: 'inline-block',
                        height: 10,
                        width: 10,
                        borderRadius: '50%',
                        backgroundColor: getReminderColor(viewEvent),
                        boxShadow: '0 0 0 2px #fff'
                      }}></span>
                      <span style={{
                        backgroundColor: `${getReminderColor(viewEvent)}18`,
                        color: getReminderColor(viewEvent),
                        padding: '3px 10px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 800
                      }}>{viewEvent.category}</span>
                      {viewEvent.recurrence && viewEvent.recurrence !== 'select' && (
                        <span style={{
                          backgroundColor: '#f1f3f7',
                          color: '#495057',
                          padding: '3px 10px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6
                        }}>
                          <i className="fas fa-redo" style={{ fontSize: 10 }}></i>
                          {viewEvent.recurrence}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div style={{ height: 1, background: '#f1f3f5', margin: '10px -4px 14px' }}></div>

              {/* Details */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                {viewEvent.start && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#495057', fontSize: 14, background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 10, padding: 10 }}>
                    <i className="far fa-clock" style={{ color: 'var(--pmr-primary)' }}></i>
                    <div>
                      <div style={{ fontWeight: 700 }}>Time</div>
                      <div style={{ opacity: 0.9 }}>{new Date(viewEvent.start).toLocaleDateString()} {new Date(viewEvent.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                    </div>
                  </div>
                )}
                {viewEvent.organizer && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#495057', fontSize: 14, background: '#fff', border: '1px solid #e9ecef', borderRadius: 10, padding: 10 }}>
                    <i className="far fa-user" style={{ color: '#6c757d' }}></i>
                    <div>
                      <div style={{ fontWeight: 700 }}>Organizer</div>
                      <div style={{ opacity: 0.9 }}>{viewEvent.organizer}</div>
                    </div>
                  </div>
                )}
                {viewEvent.location && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#495057', fontSize: 14, background: '#fff', border: '1px solid #e9ecef', borderRadius: 10, padding: 10 }}>
                    <i className="fas fa-map-marker-alt" style={{ color: '#dc3545' }}></i>
                    <div>
                      <div style={{ fontWeight: 700 }}>Location</div>
                      <div style={{ opacity: 0.9 }}>{viewEvent.location}</div>
                    </div>
                  </div>
                )}
                {viewEvent.description && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, color: '#495057', fontSize: 14, background: '#f8f9fa', borderLeft: `4px solid ${getReminderColor(viewEvent)}`, border: '1px solid #e9ecef', borderLeftColor: getReminderColor(viewEvent), borderRadius: 10, padding: 10, gridColumn: '1 / -1' }}>
                    <i className="far fa-sticky-note" style={{ color: '#6c757d', marginTop: 2 }}></i>
                    <div>
                      <div style={{ fontWeight: 700 }}>Details</div>
                      <div style={{ opacity: 0.9, lineHeight: 1.5 }}>{viewEvent.description}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
                <button
                  onClick={() => {
                    setShowEventViewModal(false);
                    setViewEvent(null);
                    openEditReminder(viewEvent);
                  }}
                  style={{
                    background: 'var(--pmr-btn-primary)',
                    color: 'var(--pmr-white)',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(var(--pmr-btn-primary-rgb), 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  <i className="fas fa-edit" style={{ fontSize: 12 }}></i>
                  Edit
                </button>
                <button
                  onClick={() => {
                    setShowEventViewModal(false);
                    setViewEvent(null);
                  }}
                  style={{
                    background: 'var(--pmr-danger)',
                    color: 'var(--pmr-white)',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(var(--pmr-danger-rgb), 0.2)'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Health Day Panel */}
      {showHealthPanel && healthPanelDate && (() => {
        const day = getDayHealth(healthPanelDate);
        const v = day.vitals;
        const bp = v ? bpStatus(v.systolic, v.diastolic) : null;
        const dateLabel = new Date(healthPanelDate).toLocaleDateString(undefined, {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
        });
        const canNavigate = typeof onNavigateToReports === 'function';

        const panelStart = new Date(healthPanelDate);
        panelStart.setHours(0, 0, 0, 0);
        const panelEnd = new Date(healthPanelDate);
        panelEnd.setHours(23, 59, 59, 999);
        const dayReminders = (safeDisplayEvents || []).filter((ev) => {
          if (!ev?.start) return false;
          const s = new Date(ev.start);
          return s >= panelStart && s <= panelEnd;
        });

        return (
          <div className="hc-panel-overlay" onClick={() => setShowHealthPanel(false)}>
            <div className="hc-panel" onClick={(e) => e.stopPropagation()}>
              <div className="hc-panel__header">
                <div>
                  <h3 className="hc-panel__title">Day Summary</h3>
                  <div className="hc-panel__subtitle">{dateLabel}</div>
                </div>
                <button className="hc-panel__close" onClick={() => setShowHealthPanel(false)} aria-label="Close">
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="hc-panel__body">
                {/* Upcoming Appointments */}
                <div className="hc-section-title">
                  <i className="fas fa-calendar-check" style={{ color: '#7c3aed' }}></i> Upcoming Appointments
                  {day.appointments.length > 0 && (
                    <span className="hc-section-count hc-section-count--appt">{day.appointments.length}</span>
                  )}
                </div>
                {day.appointments.length > 0 ? (
                  <div className={`hc-section-list${day.appointments.length > 2 ? ' hc-section-list--scroll' : ''}`}>
                    {day.appointments.map((a, i) => {
                    const title = a.location || 'Appointment';
                    const details = a.locationDetails
                      ? String(a.locationDetails)
                          .replace(/\r\n/g, ', ')
                          .split(',')
                          .map((part) => part.trim())
                          .filter((part) => {
                            if (!part || part.toLowerCase() === 'string') return false;
                            // Don't repeat the location title as subtitle
                            return part.toLowerCase() !== title.toLowerCase();
                          })
                          .slice(0, 2)
                          .join(', ')
                      : '';
                    const timeRange = a.startLabel || '';
                    return (
                      <div className="hc-rem-card hc-rem-card--appt" key={a.id || `appt-${i}`}>
                        <div className="hc-rem-card__icon" style={{ background: '#7c3aed' }}>
                          <i className="fas fa-calendar-check"></i>
                        </div>
                        <div className="hc-rem-card__body">
                          <div className="hc-rem-card__row">
                            <span className="hc-rem-card__title">{title}</span>
                            {timeRange && (
                              <span className="hc-rem-badge" style={{ background: '#efe7ff', color: '#6d28d9' }}>
                                {timeRange}
                              </span>
                            )}
                          </div>
                          {details ? <div className="hc-rem-card__sub">{details}</div> : null}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                ) : (
                  <div className="hc-empty">No upcoming appointments</div>
                )}

                {/* Reminders */}
                <div className="hc-section-title">
                  <i className="fas fa-bell" style={{ color: 'var(--pmr-primary)' }}></i> Reminders
                  {dayReminders.length > 0 && (
                    <span className="hc-section-count">{dayReminders.length}</span>
                  )}
                </div>
                {dayReminders.length > 0 ? (
                  <div className={`hc-section-list${dayReminders.length > 2 ? ' hc-section-list--scroll' : ''}`}>
                    {dayReminders.map((ev, i) => {
                    const remColor = getReminderColor(ev);
                    const recurrenceMap = {
                      daily: 'Daily',
                      weekly: 'Weekly',
                      monthly: 'Monthly',
                      yearly: 'Yearly',
                    };
                    const recurrenceLabel = ev.recurrence && ev.recurrence !== 'select'
                      ? (recurrenceMap[String(ev.recurrence).toLowerCase()] || ev.recurrence)
                      : null;
                    const startTime = ev.start
                      ? new Date(ev.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
                      : null;
                    // Day Summary already shows the date in the header — time only here

                    return (
                      <div
                        className="hc-rem-card"
                        key={ev.id || `rem-${i}`}
                        style={{ borderLeftColor: remColor }}
                      >
                        <div className="hc-rem-card__icon" style={{ background: remColor }}>
                          <i className="fas fa-bell"></i>
                        </div>
                        <div className="hc-rem-card__body">
                          <div className="hc-rem-card__row">
                            <span className="hc-rem-card__title">{ev.title || 'Untitled Reminder'}</span>
                            {startTime && (
                              <span className="hc-rem-badge" style={{ background: `${remColor}16`, color: remColor }}>
                                {startTime}
                              </span>
                            )}
                            {ev.category && ev.category !== 'Select' && (
                              <span className="hc-rem-badge hc-rem-badge--muted">{ev.category}</span>
                            )}
                            {recurrenceLabel && (
                              <span className="hc-rem-badge hc-rem-badge--muted">
                                <i className="fas fa-sync-alt"></i>
                                {recurrenceLabel}
                              </span>
                            )}
                          </div>
                          {ev.description ? (
                            <div className="hc-rem-card__note">{ev.description}</div>
                          ) : null}
                        </div>
                        <div className="hc-list-actions">
                          <button
                            type="button"
                            className="hc-list-action-btn hc-list-action-btn--edit"
                            title="Edit reminder"
                            aria-label="Edit reminder"
                            onClick={() => {
                              setShowHealthPanel(false);
                              openEditReminder(ev);
                            }}
                          >
                            <i className="fas fa-pen"></i>
                          </button>
                          <button
                            type="button"
                            className="hc-list-action-btn hc-list-action-btn--delete"
                            title="Delete reminder"
                            aria-label="Delete reminder"
                            onClick={() => showDeleteConfirmation(ev, 'single')}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                ) : (
                  <div className="hc-empty">No reminders</div>
                )}

                {/* Vitals — compact card grid (not list) */}
                <div className="hc-section-title">
                  <i className="fas fa-heart-pulse" style={{ color: '#e35336' }}></i> Vitals
                </div>
                {v ? (
                  <div className="hc-vitals-grid">
                    <div className="hc-vital-card">
                      <span className="hc-vital-card__label">
                        <i className="fas fa-heart-pulse" style={{ color: '#e35336' }}></i> Blood Pressure
                      </span>
                      <span className="hc-vital-card__value">
                        {v.systolic && v.diastolic ? `${v.systolic}/${v.diastolic}` : 'N/A'}
                        <span className="hc-vital-card__unit"> mmHg</span>
                      </span>
                      {bp && <span className="hc-badge" style={{ background: bp.color }}>{bp.label}</span>}
                    </div>
                    <div className="hc-vital-card">
                      <span className="hc-vital-card__label">
                        <i className="fas fa-heart" style={{ color: '#e35336' }}></i> Heart Rate
                      </span>
                      <span className="hc-vital-card__value">
                        {v.heartRate || 'N/A'}
                        <span className="hc-vital-card__unit"> bpm</span>
                      </span>
                    </div>
                    <div className="hc-vital-card">
                      <span className="hc-vital-card__label">
                        <i className="fas fa-droplet" style={{ color: '#2563eb' }}></i> Blood Sugar
                      </span>
                      <span className="hc-vital-card__value">
                        {v.bloodSugar || 'N/A'}
                        <span className="hc-vital-card__unit"> mg/dL</span>
                      </span>
                    </div>
                    <div className="hc-vital-card">
                      <span className="hc-vital-card__label">
                        <i className="fas fa-weight-scale" style={{ color: '#2d6a4f' }}></i> Weight
                      </span>
                      <span className="hc-vital-card__value">
                        {v.weight || 'N/A'}
                        <span className="hc-vital-card__unit"> lbs</span>
                      </span>
                    </div>
                    <div className="hc-vital-card">
                      <span className="hc-vital-card__label">
                        <i className="fas fa-ruler-vertical" style={{ color: '#8a5a48' }}></i> Waist
                      </span>
                      <span className="hc-vital-card__value">
                        {v.waist || 'N/A'}
                        <span className="hc-vital-card__unit"> in</span>
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="hc-empty">No vitals recorded</div>
                )}

                {/* Medications */}
                <div className="hc-section-title">
                  <i className="fas fa-pills" style={{ color: '#2d6a4f' }}></i> Medication Schedule
                  {day.meds.length > 0 && (
                    <span className="hc-section-count" style={{ background: 'rgba(45,106,79,0.12)', color: '#2d6a4f' }}>
                      {day.meds.length}
                    </span>
                  )}
                </div>
                {day.meds.length > 0 ? (
                  <div className={`hc-section-list${day.meds.length > 2 ? ' hc-section-list--scroll' : ''}`}>
                    {day.meds.map((m, i) => (
                    <div className="hc-rem-card hc-rem-card--med" key={`med-${i}`}>
                      <div className="hc-rem-card__icon" style={{ background: '#2d6a4f' }}>
                        <i className="fas fa-pills"></i>
                      </div>
                      <div className="hc-rem-card__body">
                        <div className="hc-rem-card__row">
                          <span className="hc-rem-card__title">{m.MedicationName || m.medication || 'Medication'}</span>
                          {(m.Dose || m.dose) && (
                            <span className="hc-rem-badge" style={{ background: '#e8f5ef', color: '#1b4332' }}>
                              {m.Dose || m.dose}
                            </span>
                          )}
                        </div>
                        <div className="hc-rem-card__sub">
                          {m.MedicationRecurrence || m.frequency || 'As prescribed'}
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
                ) : (
                  <div className="hc-empty">No medications scheduled</div>
                )}

                {/* Provider notes / reports */}
                <div className="hc-section-title">
                  <i className="fas fa-file-medical" style={{ color: '#2563eb' }}></i> Provider Notes &amp; Reports
                  {day.reports.length > 0 && (
                    <span className="hc-section-count" style={{ background: 'rgba(37,99,235,0.12)', color: '#1e40af' }}>
                      {day.reports.length}
                    </span>
                  )}
                </div>
                {day.reports.length > 0 ? (
                  <div className={`hc-section-list${day.reports.length > 2 ? ' hc-section-list--scroll' : ''}`}>
                    {day.reports.map((r, i) => {
                      const category = r.group_name || r.category || '';
                      const catStyle = providerNoteCategoryStyle(category);
                      const title = r.name || r.report_name || 'Provider note';
                      const meta = [r.provider_name, r.clinic].filter(Boolean).join(' · ');
                      return (
                        <div
                          className={`hc-rem-card hc-rem-card--note hc-note-card ${canNavigate ? 'hc-rem-card--clickable' : ''}`}
                          key={`rep-${r.reportId || i}`}
                          style={{ borderLeftColor: catStyle.accent }}
                          onClick={() => {
                            if (canNavigate) {
                              onNavigateToReports(healthPanelDate, r);
                              setShowHealthPanel(false);
                            }
                          }}
                        >
                          <div className="hc-rem-card__icon" style={{ background: catStyle.accent }}>
                            <i className="fas fa-file-medical"></i>
                          </div>
                          <div className="hc-rem-card__body">
                            <div className="hc-rem-card__row">
                              <span className="hc-rem-card__title" title={title}>{title}</span>
                              {category && (
                                <span
                                  className="hc-rem-badge hc-note-card__category"
                                  style={{ background: catStyle.soft, color: catStyle.text }}
                                >
                                  {category}
                                </span>
                              )}
                            </div>
                            {meta ? (
                              <div className="hc-rem-card__sub">{meta}</div>
                            ) : (
                              <div className="hc-rem-card__sub">Provider note</div>
                            )}
                          </div>
                          {canNavigate && <i className="fas fa-chevron-right hc-rem-card__chevron"></i>}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="hc-empty">No provider notes</div>
                )}

                <div className="hc-panel__footer">
                  <button
                    type="button"
                    className="hc-btn-ghost"
                    onClick={() => setShowHealthPanel(false)}
                  >
                    Close
                  </button>
                  {/* <button
                    type="button"
                    className="hc-btn-export"
                    title="Export this day's reminders, vitals, medications and provider notes as CSV"
                    onClick={() => exportDaySummaryDetailedCsv(healthPanelDate, dayReminders, day)}
                  >
                    <i className="fas fa-file-csv"></i>
                    <span>Export Detailed CSV</span>
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        noteNumber={deleteType === 'single' ? selectedEvent?.title || 'this reminder' : 'all reminders'}
        prefix="Reminder"
      />
    </div>
  );
};

export default Scheduler;
